import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'

import {
    Button,
    Card,
    Col,
    DatePicker,
    Divider,
    Form,
    Image,
    Input,
    InputNumber,
    message,
    Modal,
    Popconfirm,
    Row,
    Select,
    Space,
    Switch,
    Table,
    Tabs,
    Tag,
    Typography,
    Upload,
    Alert,
} from 'antd'

import {
    ClearOutlined,
    DeleteOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    SaveOutlined,
    UploadOutlined,
} from '@ant-design/icons'

import axios from 'axios'

import { env } from 'configs/EnvironmentConfig'
import { AUTH_TOKEN } from 'constants/AuthConstant'
import { useNavigate, useSearchParams } from 'react-router-dom'
import CardsTab from './CardsTab'

import UploadService from 'services/UploadService'

const { Title, Text } = Typography
const { TabPane } = Tabs

const API_BASE_URL = env.API_ENDPOINT_URL

const getImageUrl = (value) => {
    if (!value) {
        return ''
    }

    if (
        value.startsWith('http://') ||
        value.startsWith('https://') ||
        value.startsWith('data:')
    ) {
        return value
    }

    return `${API_BASE_URL}${value}`
}

// ======================================================
// CAMPO DE ÁUDIO
// ======================================================

const AudioField = ({
    form,
    label,
    fieldName,
    uploadingAudio,
    handleAudioUpload,
    previewAudio,
}) => {
    const currentUrl =
        Form.useWatch(
            fieldName,
            form,
        )

    const handleRemove =
        () => {
            form.setFieldValue(
                fieldName,
                '',
            )

            message.success(
                'Áudio removido da configuração.',
            )
        }

    return (
        <Form.Item
            label={
                label
            }
        >
            <Space
                direction="vertical"
                style={{
                    width:
                        '100%',
                }}
            >
                <Form.Item
                    name={
                        fieldName
                    }
                    noStyle
                >
                    <Input
                        readOnly
                        placeholder="Nenhum áudio enviado"
                    />
                </Form.Item>

                <Space
                    wrap
                >
                    <Upload
                        accept=".mp3,.wav,.ogg,audio/*"
                        showUploadList={
                            false
                        }
                        beforeUpload={(
                            file,
                        ) =>
                            handleAudioUpload(
                                fieldName,
                                file,
                            )
                        }
                    >
                        <Button
                            icon={
                                <UploadOutlined />
                            }
                            loading={
                                uploadingAudio ===
                                fieldName
                            }
                        >
                            {currentUrl
                                ? 'Trocar áudio'
                                : 'Enviar áudio'}
                        </Button>
                    </Upload>

                    <Button
                        icon={
                            <PlayCircleOutlined />
                        }
                        disabled={
                            !currentUrl
                        }
                        onClick={() =>
                            previewAudio(
                                currentUrl,
                            )
                        }
                    >
                        Ouvir
                    </Button>

                    <Button
                        danger
                        icon={
                            <DeleteOutlined />
                        }
                        disabled={
                            !currentUrl
                        }
                        onClick={
                            handleRemove
                        }
                    >
                        Remover
                    </Button>
                </Space>
            </Space>
        </Form.Item>
    )
}

const MemorySettings = () => {
    const [form] = Form.useForm()


    const navigate = useNavigate()

    const [searchParams] =
        useSearchParams()

    const urlGameId =
        searchParams.get('gameId')

    const [games, setGames] =
        useState([])

    const [
        selectedGameId,
        setSelectedGameId,
    ] = useState(
        urlGameId || null,
    )

    const [config, setConfig] =
        useState(null)

    const [cards, setCards] =
        useState([])

    const [
        loadingGames,
        setLoadingGames,
    ] = useState(false)

    const [
        ranking,
        setRanking,
    ] = useState([])

    const [
        loadingRanking,
        setLoadingRanking,
    ] = useState(false)

    const [
        rankingLimit,
        setRankingLimit,
    ] = useState(20)

    const [
        matches,
        setMatches,
    ] = useState([])

    const [
        loadingMatches,
        setLoadingMatches,
    ] = useState(false)

    const [
        matchesLimit,
        setMatchesLimit,
    ] = useState(100)

    const [
        clearingRanking,
        setClearingRanking,
    ] = useState(false)

    const [
        historySearch,
        setHistorySearch,
    ] = useState('')

    const [
        historyStatus,
        setHistoryStatus,
    ] = useState('ALL')

    const [
        historyDate,
        setHistoryDate,
    ] = useState(null)

    const [
        loadingConfig,
        setLoadingConfig,
    ] = useState(false)

    const [
        savingConfig,
        setSavingConfig,
    ] = useState(false)

    const [
        prizeRules,
        setPrizeRules,
    ] = useState([])

    const [
        loadingPrizeRules,
        setLoadingPrizeRules,
    ] = useState(false)

    const [
        savingPrizeRule,
        setSavingPrizeRule,
    ] = useState(false)

    const [
        prizeRuleModalOpen,
        setPrizeRuleModalOpen,
    ] = useState(false)

    const [
        editingPrizeRule,
        setEditingPrizeRule,
    ] = useState(null)

    const [
        availablePrizes,
        setAvailablePrizes,
    ] = useState([])

    const [
        loadingPrizes,
        setLoadingPrizes,
    ] = useState(false)

    const [
        prizeSourceType,
        setPrizeSourceType,
    ] = useState(
        'MEMORY',
    )

    const [
        prizeSourceGameId,
        setPrizeSourceGameId,
    ] = useState(
        null,
    )

    const [
        rouletteGames,
        setRouletteGames,
    ] = useState([])

    const [
        loadingRouletteGames,
        setLoadingRouletteGames,
    ] = useState(false)

    const [
        prizeRuleForm,
    ] = Form.useForm()

    const [
        uploadingCardBack,
        setUploadingCardBack,
    ] = useState(false)

    const [
        uploadingStartBackground,
        setUploadingStartBackground,
    ] = useState(false)

    const [
        uploadingStartLogo,
        setUploadingStartLogo,
    ] = useState(false)

    const [
        uploadingGameplayBackground,
        setUploadingGameplayBackground,
    ] = useState(false)

    const [
        uploadingGameplayLogo,
        setUploadingGameplayLogo,
    ] = useState(false)

    const [
        uploadingResultBackground,
        setUploadingResultBackground,
    ] = useState(false)

    const [
        uploadingResultLogo,
        setUploadingResultLogo,
    ] = useState(false)

    const [
        uploadingResultCompletedImage,
        setUploadingResultCompletedImage,
    ] = useState(false)

    const [
        uploadingResultTimeoutImage,
        setUploadingResultTimeoutImage,
    ] = useState(false)

    const [
        uploadingAudio,
        setUploadingAudio,
    ] = useState(null)

    const selectedGame =
        useMemo(
            () =>
                games.find(
                    (game) =>
                        game.id ===
                        selectedGameId,
                ) || null,
            [
                games,
                selectedGameId,
            ],
        )

    const axiosConfig =
        useMemo(() => {
            const jwtToken =
                localStorage.getItem(
                    AUTH_TOKEN,
                ) ||
                localStorage.getItem(
                    'AUTH_TOKEN',
                ) ||
                localStorage.getItem(
                    'ACCESS_TOKEN',
                )

            const clientId =
                localStorage.getItem(
                    'CLIENT_ID',
                )

            const headers = {}

            if (jwtToken) {
                headers.Authorization =
                    `Bearer ${jwtToken}`
            }

            if (clientId) {
                headers[
                    'x-client-id'
                ] = clientId
            }

            return {
                headers,
            }
        }, [])

    // ======================================================
    // CARREGAR JOGOS
    // ======================================================

    const loadGames =
        useCallback(
            async () => {
                try {
                    setLoadingGames(
                        true,
                    )

                    const response =
                        await axios.get(
                            `${API_BASE_URL}/games`,
                            axiosConfig,
                        )

                    const data =
                        Array.isArray(
                            response.data,
                        )
                            ? response.data
                            : response
                                .data
                                ?.items ||
                            response
                                .data
                                ?.data ||
                            []

                    const memoryGames =
                        data.filter(
                            (
                                game,
                            ) =>
                                String(
                                    game?.type ||
                                    '',
                                ).toUpperCase() ===
                                'MEMORY',
                        )

                    setGames(
                        memoryGames,
                    )

                    if (
                        urlGameId
                    ) {
                        const exists =
                            memoryGames.some(
                                (
                                    game,
                                ) =>
                                    game.id ===
                                    urlGameId,
                            )

                        if (
                            exists
                        ) {
                            setSelectedGameId(
                                urlGameId,
                            )

                            return
                        }
                    }

                    if (
                        !selectedGameId &&
                        memoryGames.length >
                        0
                    ) {
                        setSelectedGameId(
                            memoryGames[0]
                                .id,
                        )
                    }
                } catch (
                error
                ) {
                    console.error(
                        'Erro ao carregar jogos:',
                        error,
                    )

                    message.error(
                        'Não foi possível carregar os jogos.',
                    )
                } finally {
                    setLoadingGames(
                        false,
                    )
                }
            },
            [
                axiosConfig,
                selectedGameId,
                urlGameId,
            ],
        )

    // ======================================================
    // CARREGAR CONFIGURAÇÃO
    // ======================================================

    const loadConfig =
        useCallback(
            async (
                gameId,
            ) => {
                if (
                    !gameId
                ) {
                    setConfig(
                        null,
                    )

                    setCards([])

                    form.resetFields()

                    return
                }

                try {
                    setLoadingConfig(
                        true,
                    )

                    const response =
                        await axios.get(
                            `${API_BASE_URL}/memory-games/${gameId}`,
                            axiosConfig,
                        )

                    const responseConfig =
                        response.data
                            ?.config ||
                        null

                    const responseGame =
                        response.data
                            ?.game ||
                        null

                    setConfig(
                        responseConfig,
                    )

                    setCards(
                        Array.isArray(
                            responseConfig?.cards,
                        )
                            ? responseConfig.cards
                            : [],
                    )

                    form.setFieldsValue({
                        pairCount:
                            responseConfig?.pairCount ??
                            4,

                        timeLimit:
                            responseConfig?.timeLimit ??
                            60,

                        revealDuration:
                            responseConfig?.revealDuration ??
                            800,

                        revealAtStart:
                            responseConfig?.revealAtStart ??
                            true,

                        revealStartDuration:
                            responseConfig?.revealStartDuration ??
                            5000,

                        revealCountdownEnabled:
                            responseConfig?.revealCountdownEnabled ??
                            true,

                        timerStartsAfterReveal:
                            responseConfig?.timerStartsAfterReveal ??
                            true,

                        introEnabled:
                            responseConfig?.introEnabled ??
                            true,

                        introPrepareText:
                            responseConfig?.introPrepareText ||
                            'PREPARE-SE',

                        introMemorizeText:
                            responseConfig?.introMemorizeText ||
                            'MEMORIZE AS CARTAS',

                        introGoText:
                            responseConfig?.introGoText ||
                            'VALENDO!',

                        introPrepareDuration:
                            responseConfig?.introPrepareDuration ??
                            1200,

                        introGoDuration:
                            responseConfig?.introGoDuration ??
                            900,

                        showTimer:
                            responseConfig?.showTimer ??
                            true,

                        showAttempts:
                            responseConfig?.showAttempts ??
                            true,

                        showScore:
                            responseConfig?.showScore ??
                            true,

                        showRanking:
                            responseConfig?.showRanking ??
                            true,

                        prizeEnabled:
                            responseConfig?.prizeEnabled ??
                            false,

                        prizeRuleType:
                            responseConfig?.prizeRuleType ||
                            'NONE',

                        cardBackImageUrl:
                            responseConfig?.cardBackImageUrl ||
                            '',

                        cardBackColor:
                            responseConfig?.cardBackColor ||
                            '#1677ff',

                        cardFrontColor:
                            responseConfig?.cardFrontColor ||
                            '#ffffff',

                        cardBorderColor:
                            responseConfig?.cardBorderColor ||
                            '#e5e7eb',

                        cardBorderWidth:
                            responseConfig?.cardBorderWidth ??
                            2,

                        cardBorderRadius:
                            responseConfig?.cardBorderRadius ??
                            16,

                        cardGap:
                            responseConfig?.cardGap ??
                            16,

                        cardSize:
                            responseConfig?.cardSize ??
                            160,

                        cardWidth:
                            responseConfig?.cardWidth ??
                            160,

                        boardMaxWidth:
                            responseConfig?.boardMaxWidth ??
                            900,

                        layoutOrientation:
                            responseConfig?.layoutOrientation ||
                            'AUTO',

                        portraitColumns:
                            responseConfig?.portraitColumns ??
                            4,

                        landscapeColumns:
                            responseConfig?.landscapeColumns ??
                            8,

                        cardShadow:
                            responseConfig?.cardShadow ??
                            true,

                        showGameLogo:
                            responseConfig?.showGameLogo ??
                            true,

                        gameLogoWidth:
                            responseGame?.gameLogoWidth ??
                            110,

                        gameLogoHeight:
                            responseGame?.gameLogoHeight ??
                            80,

                        gameplayBackgroundUrl:
                            responseGame?.gameplayBackgroundUrl ||
                            '',

                        gameplayBackgroundColor:
                            responseGame?.gameplayBackgroundColor ||
                            responseGame?.backgroundColor ||
                            '#ffffff',

                        gameplayLogoUrl:
                            responseGame?.gameplayLogoUrl ||
                            '',

                        gameplayPrimaryColor:
                            responseGame?.gameplayPrimaryColor ||
                            responseGame?.primaryColor ||
                            '#1677ff',

                        gameplayTextColor:
                            responseGame?.gameplayTextColor ||
                            responseGame?.textColor ||
                            '#111827',

                        backgroundFit:
                            responseGame?.backgroundFit ||
                            'cover',

                        hudBackground:
                            responseGame?.hudBackground ||
                            'rgba(255,255,255,.94)',

                        hudTextColor:
                            responseGame?.hudTextColor ||
                            '#0d3f9f',

                        hudLabelColor:
                            responseGame?.hudLabelColor ||
                            '#0d3f9f',

                        hudBorderRadius:
                            responseGame?.hudBorderRadius ??
                            999,

                        hudShadow:
                            responseGame?.hudShadow ??
                            true,

                        cardImageFit:
                            responseGame?.cardImageFit ||
                            'fill',

                        matchEffectEnabled:
                            responseConfig?.matchEffectEnabled ??
                            true,

                        matchEffectDuration:
                            responseConfig?.matchEffectDuration ??
                            650,

                        matchGlowColor:
                            responseConfig?.matchGlowColor ||
                            '#52c41a',

                        matchedCardBehavior:
                            responseConfig?.matchedCardBehavior ||
                            'DIM',

                        matchedCardOpacity:
                            responseConfig?.matchedCardOpacity ??
                            45,

                        comboEnabled:
                            responseConfig?.comboEnabled ??
                            true,

                        comboBaseMultiplier:
                            responseConfig?.comboBaseMultiplier ??
                            0.5,

                        comboMaxMultiplier:
                            responseConfig?.comboMaxMultiplier ??
                            5,

                        comboDisplayDuration:
                            responseConfig?.comboDisplayDuration ??
                            900,

                        comboColor:
                            responseConfig?.comboColor ||
                            '#fadb14',

                        scoreMode:
                            responseConfig?.scoreMode ||
                            'TIME_ERRORS',

                        pointsPerMatch:
                            responseConfig?.pointsPerMatch ??
                            100,

                        errorPenalty:
                            responseConfig?.errorPenalty ??
                            50,

                        timePenaltyPerSec:
                            responseConfig?.timePenaltyPerSec ??
                            10,

                        comboAffectsScore:
                            responseConfig?.comboAffectsScore ??
                            true,

                        soundEnabled:
                            responseConfig?.soundEnabled ??
                            true,

                        musicEnabled:
                            responseConfig?.musicEnabled ??
                            false,

                        flipSoundUrl:
                            responseConfig?.flipSoundUrl ||
                            '',

                        matchSoundUrl:
                            responseConfig?.matchSoundUrl ||
                            '',

                        errorSoundUrl:
                            responseConfig?.errorSoundUrl ||
                            '',

                        countdownSoundUrl:
                            responseConfig?.countdownSoundUrl ||
                            '',

                        goSoundUrl:
                            responseConfig?.goSoundUrl ||
                            '',

                        completeSoundUrl:
                            responseConfig?.completeSoundUrl ||
                            '',

                        musicUrl:
                            responseConfig?.musicUrl ||
                            '',

                        soundVolume:
                            responseConfig?.soundVolume ??
                            0.8,

                        musicVolume:
                            responseConfig?.musicVolume ??
                            0.4,

                        startScreenEnabled:
                            responseConfig?.startScreenEnabled ??
                            true,

                        startScreenBackgroundUrl:
                            responseConfig?.startScreenBackgroundUrl ||
                            '',

                        startScreenBackgroundColor:
                            responseConfig?.startScreenBackgroundColor ||
                            '#ffffff',

                        startScreenLogoUrl:
                            responseConfig?.startScreenLogoUrl ||
                            '',

                        startScreenShowLogo:
                            responseConfig?.startScreenShowLogo ??
                            true,

                        startScreenLogoWidth:
                            responseConfig?.startScreenLogoWidth ??
                            220,

                        startScreenLogoHeight:
                            responseConfig?.startScreenLogoHeight ??
                            220,

                        startScreenTitle:
                            responseConfig?.startScreenTitle ||
                            'DESAFIO DA MEMÓRIA',

                        startScreenShowTitle:
                            responseConfig?.startScreenShowTitle ??
                            true,

                        startScreenTitleColor:
                            responseConfig?.startScreenTitleColor ||
                            '#0d3f9f',

                        startScreenTitleFontSize:
                            responseConfig?.startScreenTitleFontSize ??
                            76,

                        startScreenSubtitle:
                            responseConfig?.startScreenSubtitle ||
                            'Memorize as cartas e encontre todos os pares no menor tempo possível!',

                        startScreenShowSubtitle:
                            responseConfig?.startScreenShowSubtitle ??
                            true,

                        startScreenSubtitleColor:
                            responseConfig?.startScreenSubtitleColor ||
                            '#0d3f9f',

                        startScreenSubtitleFontSize:
                            responseConfig?.startScreenSubtitleFontSize ??
                            28,

                        startScreenButtonText:
                            responseConfig?.startScreenButtonText ||
                            'JOGAR',

                        startScreenPrimaryColor:
                            responseConfig?.startScreenPrimaryColor ||
                            '#1677ff',

                        startScreenButtonTextColor:
                            responseConfig?.startScreenButtonTextColor ||
                            '#1677ff',

                        startScreenPlayButtonSize:
                            responseConfig?.startScreenPlayButtonSize ??
                            150,

                        startScreenHintText:
                            responseConfig?.startScreenHintText ||
                            'Toque para iniciar',

                        startScreenShowHint:
                            responseConfig?.startScreenShowHint ??
                            true,

                        startScreenHintColor:
                            responseConfig?.startScreenHintColor ||
                            '#0d3f9f',

                        startScreenHintFontSize:
                            responseConfig?.startScreenHintFontSize ??
                            21,

                        // ======================================================
                        // RESULTADO
                        // ======================================================

                        resultImageOnly:
                            responseConfig?.resultImageOnly ??
                            false,

                        resultCompletedImageUrl:
                            responseConfig?.resultCompletedImageUrl ||
                            '',

                        resultTimeoutImageUrl:
                            responseConfig?.resultTimeoutImageUrl ||
                            '',

                        resultShowBackButton:
                            responseConfig?.resultShowBackButton ??
                            true,

                        resultBackgroundUrl:
                            responseConfig?.resultBackgroundUrl ||
                            '',

                        resultBackgroundColor:
                            responseConfig?.resultBackgroundColor ||
                            '#ffffff',

                        resultLogoUrl:
                            responseConfig?.resultLogoUrl ||
                            '',

                        resultShowLogo:
                            responseConfig?.resultShowLogo ??
                            true,

                        resultLogoWidth:
                            responseConfig?.resultLogoWidth ??
                            180,

                        resultLogoHeight:
                            responseConfig?.resultLogoHeight ??
                            100,

                        resultCompletedTitle:
                            responseConfig?.resultCompletedTitle ||
                            'PARABÉNS!',

                        resultTimeoutTitle:
                            responseConfig?.resultTimeoutTitle ||
                            'TEMPO ESGOTADO!',

                        resultSubtitle:
                            responseConfig?.resultSubtitle ||
                            'Confira seu resultado',

                        resultTitleColor:
                            responseConfig?.resultTitleColor ||
                            '#0d3f9f',

                        resultTitleFontSize:
                            responseConfig?.resultTitleFontSize ??
                            64,

                        resultTextColor:
                            responseConfig?.resultTextColor ||
                            '#111827',

                        resultPrimaryColor:
                            responseConfig?.resultPrimaryColor ||
                            '#1677ff',

                        resultCardBackground:
                            responseConfig?.resultCardBackground ||
                            '#ffffff',

                        resultCardTextColor:
                            responseConfig?.resultCardTextColor ||
                            '#111827',

                        resultCardBorderRadius:
                            responseConfig?.resultCardBorderRadius ??
                            24,

                        resultCardShadow:
                            responseConfig?.resultCardShadow ??
                            true,

                        resultShowScore:
                            responseConfig?.resultShowScore ??
                            true,

                        resultShowTime:
                            responseConfig?.resultShowTime ??
                            true,

                        resultShowAttempts:
                            responseConfig?.resultShowAttempts ??
                            true,

                        resultShowErrors:
                            responseConfig?.resultShowErrors ??
                            true,

                        resultShowRanking:
                            responseConfig?.resultShowRanking ??
                            true,

                        resultRankingTitle:
                            responseConfig?.resultRankingTitle ||
                            'RANKING',

                        resultRankingLimit:
                            responseConfig?.resultRankingLimit ??
                            5,

                        resultButtonText:
                            responseConfig?.resultButtonText ||
                            'PRÓXIMO PARTICIPANTE',

                        resultButtonColor:
                            responseConfig?.resultButtonColor ||
                            '#1677ff',

                        resultButtonTextColor:
                            responseConfig?.resultButtonTextColor ||
                            '#ffffff',
                        resultExternalRedirectEnabled:
                            responseConfig?.resultExternalRedirectEnabled ??
                            false,

                        resultExternalRedirectUrl:
                            responseConfig?.resultExternalRedirectUrl ||
                            '',

                        resultExternalButtonText:
                            responseConfig?.resultExternalButtonText ||
                            'ACESSAR',

                        resultExternalOpenNewTab:
                            responseConfig?.resultExternalOpenNewTab ??
                            false,
                    })
                } catch (
                error
                ) {
                    console.error(
                        'Erro ao carregar configuração:',
                        error,
                    )

                    message.error(
                        error?.response
                            ?.data
                            ?.message ||
                        'Não foi possível carregar a configuração.',
                    )
                } finally {
                    setLoadingConfig(
                        false,
                    )
                }
            },
            [
                axiosConfig,
                form,
            ],
        )

    // ======================================================
    // CARREGAR REGRAS DE PREMIAÇÃO
    // ======================================================

    const loadPrizeRules =
        useCallback(
            async (
                gameId,
            ) => {
                if (
                    !gameId
                ) {
                    setPrizeRules(
                        [],
                    )

                    return
                }

                try {
                    setLoadingPrizeRules(
                        true,
                    )

                    const response =
                        await axios.get(
                            `${API_BASE_URL}/memory-games/${gameId}/prize-rules`,
                            axiosConfig,
                        )

                    const data =
                        Array.isArray(
                            response.data,
                        )
                            ? response.data
                            : response.data
                                ?.items ||
                            response.data
                                ?.data ||
                            []

                    setPrizeRules(
                        Array.isArray(
                            data,
                        )
                            ? data
                            : [],
                    )
                } catch (
                error
                ) {
                    console.error(
                        'Erro ao carregar regras de premiação:',
                        error,
                    )

                    setPrizeRules(
                        [],
                    )

                    message.error(
                        error?.response
                            ?.data
                            ?.message ||
                        'Não foi possível carregar as regras de premiação.',
                    )
                } finally {
                    setLoadingPrizeRules(
                        false,
                    )
                }
            },
            [
                axiosConfig,
            ],
        )

    // ======================================================
    // CARREGAR ROLETAS DISPONÍVEIS
    // ======================================================

    const loadRouletteGames =
        useCallback(
            async () => {
                try {
                    setLoadingRouletteGames(
                        true,
                    )

                    const response =
                        await axios.get(
                            `${API_BASE_URL}/games`,
                            axiosConfig,
                        )

                    const data =
                        Array.isArray(
                            response.data,
                        )
                            ? response.data
                            : response.data
                                ?.items ||
                            response.data
                                ?.data ||
                            []

                    const roulettes =
                        data.filter(
                            (
                                item,
                            ) =>
                                String(
                                    item?.type ||
                                    '',
                                ).toUpperCase() ===
                                'ROULETTE',
                        )

                    // Se o Memory estiver ligado a um evento,
                    // mostramos primeiro apenas roletas
                    // desse mesmo evento.
                    const currentMemoryGame =
                        data.find(
                            (
                                item,
                            ) =>
                                item.id ===
                                selectedGameId,
                        )

                    const currentEventId =
                        currentMemoryGame?.eventId ||
                        currentMemoryGame
                            ?.event
                            ?.id ||
                        null

                    const sameEventRoulette =
                        currentEventId
                            ? roulettes.filter(
                                (
                                    item,
                                ) =>
                                    (
                                        item?.eventId ||
                                        item?.event?.id
                                    ) ===
                                    currentEventId,
                            )
                            : roulettes

                    setRouletteGames(
                        sameEventRoulette,
                    )
                } catch (
                error
                ) {
                    console.error(
                        'Erro ao carregar roletas:',
                        error,
                    )

                    setRouletteGames(
                        [],
                    )
                } finally {
                    setLoadingRouletteGames(
                        false,
                    )
                }
            },
            [
                axiosConfig,
                selectedGameId,
            ],
        )

    // ======================================================
    // CARREGAR BRINDES DISPONÍVEIS
    // ======================================================

    const loadAvailablePrizes =
        useCallback(
            async (
                sourceGameId,
            ) => {
                if (
                    !sourceGameId
                ) {
                    setAvailablePrizes(
                        [],
                    )

                    return
                }

                try {
                    setLoadingPrizes(
                        true,
                    )

                    const response =
                        await axios.get(
                            `${API_BASE_URL}/prizes`,
                            {
                                ...axiosConfig,

                                params: {
                                    gameId:
                                        sourceGameId,
                                },
                            },
                        )

                    const data =
                        Array.isArray(
                            response.data,
                        )
                            ? response.data
                            : Array.isArray(
                                response.data
                                    ?.items,
                            )
                                ? response.data.items
                                : Array.isArray(
                                    response.data
                                        ?.data,
                                )
                                    ? response.data.data
                                    : []

                    setAvailablePrizes(
                        data,
                    )

                    console.log(
                        '[MEMORY PRIZES] Origem:',
                        sourceGameId,
                    )

                    console.log(
                        '[MEMORY PRIZES] Brindes:',
                        data,
                    )
                } catch (
                error
                ) {
                    console.error(
                        'Erro ao carregar brindes disponíveis:',
                        error,
                    )

                    setAvailablePrizes(
                        [],
                    )

                    message.error(
                        error?.response
                            ?.data
                            ?.message ||
                        'Não foi possível carregar os brindes.',
                    )
                } finally {
                    setLoadingPrizes(
                        false,
                    )
                }
            },
            [
                axiosConfig,
            ],
        )

    // ======================================================
    // NOVA REGRA DE PREMIAÇÃO
    // ======================================================

    const handleNewPrizeRule =
        async () => {
            if (
                !selectedGameId
            ) {
                message.warning(
                    'Selecione um jogo.',
                )

                return
            }

            setEditingPrizeRule(
                null,
            )

            prizeRuleForm.resetFields()

            prizeRuleForm.setFieldsValue({
                isActive:
                    true,

                priority:
                    prizeRules.length +
                    1,

                minScore:
                    null,

                maxAttempts:
                    null,

                maxTimeMs:
                    null,

                probabilityWeight:
                    100,
            })

            // Sempre abre inicialmente usando
            // os brindes do próprio Memory.
            setPrizeSourceType(
                'MEMORY',
            )

            setPrizeSourceGameId(
                selectedGameId,
            )

            setAvailablePrizes(
                [],
            )

            await loadAvailablePrizes(
                selectedGameId,
            )

            setPrizeRuleModalOpen(
                true,
            )
        }

    // ======================================================
    // EDITAR REGRA DE PREMIAÇÃO
    // ======================================================

    const handleEditPrizeRule =
        async (
            rule,
        ) => {
            setEditingPrizeRule(
                rule,
            )

            const sourceGameId =
                rule?.prize?.gameId ||
                selectedGameId

            const isMemoryPrize =
                sourceGameId ===
                selectedGameId

            const sourceType =
                isMemoryPrize
                    ? 'MEMORY'
                    : 'ROULETTE'

            setPrizeSourceType(
                sourceType,
            )

            setPrizeSourceGameId(
                sourceGameId,
            )

            if (
                sourceType ===
                'ROULETTE'
            ) {
                await loadRouletteGames()
            }

            await loadAvailablePrizes(
                sourceGameId,
            )

            prizeRuleForm.setFieldsValue({
                prizeId:
                    rule?.prizeId ||
                    rule?.prize?.id,

                priority:
                    rule?.priority ??
                    1,

                minScore:
                    rule?.minScore ??
                    null,

                maxAttempts:
                    rule?.maxAttempts ??
                    null,

                maxTimeMs:
                    rule?.maxTimeMs ??
                    null,

                probabilityWeight:
                    rule?.probabilityWeight ??
                    100,

                isActive:
                    rule?.isActive ??
                    true,
            })

            setPrizeRuleModalOpen(
                true,
            )
        }
    // ======================================================
    // SALVAR REGRA DE PREMIAÇÃO
    // ======================================================

    const handleSavePrizeRule =
        async () => {
            if (
                !selectedGameId
            ) {
                message.warning(
                    'Selecione um jogo.',
                )

                return
            }

            try {
                const values =
                    await prizeRuleForm.validateFields()

                setSavingPrizeRule(
                    true,
                )

                const payload = {
                    prizeId:
                        values.prizeId,

                    priority:
                        Number(
                            values.priority ||
                            1,
                        ),

                    minScore:
                        values.minScore ??
                        null,

                    maxAttempts:
                        values.maxAttempts ??
                        null,

                    maxTimeMs:
                        values.maxTimeMs ??
                        null,

                    probabilityWeight:
                        Number(
                            values.probabilityWeight ??
                            100,
                        ),

                    isActive:
                        values.isActive !==
                        false,
                }

                if (
                    editingPrizeRule?.id
                ) {
                    await axios.put(
                        `${API_BASE_URL}/memory-games/${selectedGameId}/prize-rules/${editingPrizeRule.id}`,
                        payload,
                        axiosConfig,
                    )
                } else {
                    await axios.post(
                        `${API_BASE_URL}/memory-games/${selectedGameId}/prize-rules`,
                        payload,
                        axiosConfig,
                    )
                }

                message.success(
                    editingPrizeRule
                        ? 'Regra atualizada com sucesso.'
                        : 'Regra criada com sucesso.',
                )

                setPrizeRuleModalOpen(
                    false,
                )

                setEditingPrizeRule(
                    null,
                )

                prizeRuleForm.resetFields()

                await loadPrizeRules(
                    selectedGameId,
                )
            } catch (error) {
                if (
                    error?.errorFields
                ) {
                    return
                }

                console.error(
                    'Erro ao salvar regra:',
                    error,
                )

                console.error(
                    'Resposta da API:',
                    error?.response?.data,
                )

                message.error(
                    error?.response
                        ?.data
                        ?.message ||
                    'Não foi possível salvar a regra.',
                )
            } finally {
                setSavingPrizeRule(
                    false,
                )
            }
        }

    useEffect(() => {
        loadGames()
    }, [loadGames])

    // ======================================================
    // EXCLUIR REGRA DE PREMIAÇÃO
    // ======================================================

    const handleDeletePrizeRule =
        async (
            ruleId,
        ) => {
            if (
                !selectedGameId ||
                !ruleId
            ) {
                return
            }

            try {
                await axios.delete(
                    `${API_BASE_URL}/memory-games/${selectedGameId}/prize-rules/${ruleId}`,
                    axiosConfig,
                )

                message.success(
                    'Regra removida com sucesso.',
                )

                await loadPrizeRules(
                    selectedGameId,
                )
            } catch (error) {
                console.error(
                    'Erro ao excluir regra:',
                    error,
                )

                message.error(
                    error?.response
                        ?.data
                        ?.message ||
                    'Não foi possível excluir a regra.',
                )
            }
        }

    // ======================================================
    // CARREGAR RANKING
    // ======================================================

    const loadRanking =
        useCallback(
            async (
                gameId,
                limit = rankingLimit,
            ) => {
                if (!gameId) {
                    setRanking([])

                    return
                }

                try {
                    setLoadingRanking(
                        true,
                    )

                    const response =
                        await axios.get(
                            `${API_BASE_URL}/memory-games/${gameId}/ranking`,
                            {
                                ...axiosConfig,

                                params: {
                                    limit,
                                },
                            },
                        )

                    const data =
                        Array.isArray(
                            response.data,
                        )
                            ? response.data
                            : response.data
                                ?.items ||
                            response.data
                                ?.data ||
                            []

                    setRanking(
                        data,
                    )
                } catch (error) {
                    console.error(
                        'Erro ao carregar ranking:',
                        error,
                    )

                    message.error(
                        error?.response
                            ?.data
                            ?.message ||
                        'Não foi possível carregar o ranking.',
                    )

                    setRanking([])
                } finally {
                    setLoadingRanking(
                        false,
                    )
                }
            },
            [
                axiosConfig,
                rankingLimit,
            ],
        )

    // ======================================================
    // CARREGAR HISTÓRICO
    // ======================================================

    const loadMatches =
        useCallback(
            async (
                gameId,
                limit = matchesLimit,
            ) => {
                if (!gameId) {
                    setMatches([])

                    return
                }

                try {
                    setLoadingMatches(
                        true,
                    )

                    const response =
                        await axios.get(
                            `${API_BASE_URL}/memory-games/${gameId}/matches`,
                            {
                                ...axiosConfig,

                                params: {
                                    limit,
                                },
                            },
                        )

                    const data =
                        Array.isArray(
                            response.data,
                        )
                            ? response.data
                            : response.data?.items ||
                            response.data?.data ||
                            []

                    setMatches(
                        data,
                    )
                } catch (error) {
                    console.error(
                        'Erro ao carregar histórico:',
                        error,
                    )

                    message.error(
                        error?.response
                            ?.data
                            ?.message ||
                        'Não foi possível carregar o histórico.',
                    )

                    setMatches([])
                } finally {
                    setLoadingMatches(
                        false,
                    )
                }
            },
            [
                axiosConfig,
                matchesLimit,
            ],
        )

    // ======================================================
    // LIMPAR RANKING
    // ======================================================

    const handleClearRanking =
        async () => {
            if (
                !selectedGameId
            ) {
                message.warning(
                    'Selecione um jogo.',
                )

                return
            }

            try {
                setClearingRanking(
                    true,
                )

                const response =
                    await axios.delete(
                        `${API_BASE_URL}/memory-games/${selectedGameId}/ranking`,
                        axiosConfig,
                    )

                const deleted =
                    Number(
                        response?.data?.deleted ||
                        0,
                    )

                setRanking([])

                message.success(
                    deleted > 0
                        ? `${deleted} partida(s) removida(s). Ranking zerado.`
                        : 'O ranking já estava vazio.',
                )

                await loadRanking(
                    selectedGameId,
                    rankingLimit,
                )
                await loadMatches(
                    selectedGameId,
                    matchesLimit,
                )
            } catch (error) {
                console.error(
                    'Erro ao limpar ranking:',
                    error,
                )

                message.error(
                    error?.response
                        ?.data
                        ?.message ||
                    'Não foi possível limpar o ranking.',
                )
            } finally {
                setClearingRanking(
                    false,
                )
            }
        }

    useEffect(() => {
        if (
            selectedGameId
        ) {
            loadConfig(
                selectedGameId,
            )

            loadPrizeRules(
                selectedGameId,
            )

            loadRouletteGames()

            // Por padrão usamos os brindes
            // do próprio Memory.
            setPrizeSourceType(
                'MEMORY',
            )

            setPrizeSourceGameId(
                selectedGameId,
            )

            loadAvailablePrizes(
                selectedGameId,
            )
        } else {
            setPrizeRules(
                [],
            )

            setAvailablePrizes(
                [],
            )

            setRouletteGames(
                [],
            )

            setPrizeSourceGameId(
                null,
            )
        }
    }, [
        selectedGameId,
        loadConfig,
        loadPrizeRules,
        loadRouletteGames,
        loadAvailablePrizes,
    ])

    useEffect(() => {
        if (
            selectedGameId
        ) {
            loadRanking(
                selectedGameId,
                rankingLimit,
            )
        } else {
            setRanking([])
        }
    }, [
        selectedGameId,
        rankingLimit,
        loadRanking,
    ])

    useEffect(() => {
        if (
            selectedGameId
        ) {
            loadMatches(
                selectedGameId,
                matchesLimit,
            )
        } else {
            setMatches([])
        }
    }, [
        selectedGameId,
        matchesLimit,
        loadMatches,
    ])

    // ======================================================
    // SALVAR CONFIGURAÇÃO
    // ======================================================

    const handleSaveConfig =
        async () => {
            if (
                !selectedGameId
            ) {
                message.warning(
                    'Selecione um jogo.',
                )

                return
            }

            try {
                await form.validateFields()

                const values =
                    form.getFieldsValue(
                        true,
                    )

                setSavingConfig(
                    true,
                )

                // ======================================================
                // CAMPOS GERAIS DO GAME
                // Estes campos pertencem à tabela/model Game
                // ======================================================

                const gamePayload = {
                    gameLogoWidth:
                        values.gameLogoWidth,

                    gameLogoHeight:
                        values.gameLogoHeight,

                    gameplayBackgroundUrl:
                        values.gameplayBackgroundUrl,

                    gameplayBackgroundColor:
                        values.gameplayBackgroundColor,

                    gameplayLogoUrl:
                        values.gameplayLogoUrl,

                    gameplayPrimaryColor:
                        values.gameplayPrimaryColor,

                    gameplayTextColor:
                        values.gameplayTextColor,

                    backgroundFit:
                        values.backgroundFit,

                    hudBackground:
                        values.hudBackground,

                    hudTextColor:
                        values.hudTextColor,

                    hudLabelColor:
                        values.hudLabelColor,

                    hudBorderRadius:
                        values.hudBorderRadius,

                    hudShadow:
                        values.hudShadow,

                    cardImageFit:
                        values.cardImageFit,
                }

                console.log(
                    '[MEMORY ADMIN] GAME PAYLOAD:',
                    gamePayload,
                )

                // ======================================================
                // CAMPOS ESPECÍFICOS DO MEMORY GAME
                // ======================================================

                const memoryPayload = {
                    ...values,
                }

                // ======================================================
                // PREMIAÇÃO
                // ======================================================

                memoryPayload.prizeEnabled =
                    values.prizeEnabled === true

                memoryPayload.prizeRuleType =
                    values.prizeEnabled === true
                        ? values.prizeRuleType ||
                        'SCORE'
                        : 'NONE'

                // ======================================================
                // REMOVER DO MEMORY OS CAMPOS QUE PERTENCEM AO GAME
                // ======================================================

                delete memoryPayload.gameLogoWidth
                delete memoryPayload.gameLogoHeight

                delete memoryPayload.gameplayBackgroundUrl
                delete memoryPayload.gameplayBackgroundColor
                delete memoryPayload.gameplayLogoUrl
                delete memoryPayload.gameplayPrimaryColor
                delete memoryPayload.gameplayTextColor

                delete memoryPayload.backgroundFit

                delete memoryPayload.hudBackground
                delete memoryPayload.hudTextColor
                delete memoryPayload.hudLabelColor
                delete memoryPayload.hudBorderRadius
                delete memoryPayload.hudShadow

                delete memoryPayload.cardImageFit

                // ======================================================
                // SALVAR CONFIGURAÇÃO ESPECÍFICA DO MEMORY
                // ======================================================

                const memoryResponse =
                    await axios.put(
                        `${API_BASE_URL}/memory-games/${selectedGameId}/config`,
                        memoryPayload,
                        axiosConfig,
                    )

                // ======================================================
                // SALVAR CONFIGURAÇÃO VISUAL GERAL DO GAME
                // ======================================================

                await axios.patch(
                    `${API_BASE_URL}/games/${selectedGameId}`,
                    gamePayload,
                    axiosConfig,
                )

                // ======================================================
                // ATUALIZAR ESTADO LOCAL
                // ======================================================

                setConfig(
                    memoryResponse.data,
                )

                if (
                    Array.isArray(
                        memoryResponse.data
                            ?.cards,
                    )
                ) {
                    setCards(
                        memoryResponse.data
                            .cards,
                    )
                }

                // ======================================================
                // RECARREGAR DADOS
                // Importante porque parte vem de MemoryGameConfig
                // e parte agora vem de Game
                // ======================================================

                await loadGames()

                await loadConfig(
                    selectedGameId,
                )

                message.success(
                    'Configuração salva com sucesso.',
                )
            } catch (
            error
            ) {
                if (
                    error?.errorFields
                ) {
                    return
                }

                console.error(
                    'Erro ao salvar configuração:',
                    error,
                )

                console.error(
                    'Resposta da API:',
                    error?.response?.data,
                )

                message.error(
                    error?.response
                        ?.data
                        ?.message ||
                    'Não foi possível salvar.',
                )
            } finally {
                setSavingConfig(
                    false,
                )
            }
        }

    // ======================================================
    // UPLOAD
    // ======================================================

    const getMediaUrl =
        (url) => {
            if (!url) {
                return null
            }

            if (
                url.startsWith('http://') ||
                url.startsWith('https://') ||
                url.startsWith('data:') ||
                url.startsWith('blob:')
            ) {
                return url
            }

            return `${API_BASE_URL}${url}`
        }

    const previewAudio =
        (url) => {
            if (!url) {
                message.warning(
                    'Nenhum áudio enviado.',
                )

                return
            }

            try {
                const audio =
                    new Audio(
                        getMediaUrl(
                            url,
                        ),
                    )

                audio.play().catch(
                    () => {
                        message.error(
                            'Não foi possível reproduzir o áudio.',
                        )
                    },
                )
            } catch (error) {
                console.error(
                    'Erro ao reproduzir áudio:',
                    error,
                )

                message.error(
                    'Não foi possível reproduzir o áudio.',
                )
            }
        }

    const uploadImage =
        async (
            file,
            setter,
        ) => {
            if (
                !file?.type?.startsWith(
                    'image/',
                )
            ) {
                message.error(
                    'Selecione um arquivo de imagem.',
                )

                return null
            }

            if (
                file.size >
                5 *
                1024 *
                1024
            ) {
                message.error(
                    'A imagem deve ter no máximo 5 MB.',
                )

                return null
            }

            try {
                setter(true)

                const response =
                    await UploadService.image(
                        file,
                    )

                const data =
                    response?.data ||
                    response

                const imageUrl =
                    data?.url ||
                    data?.path ||
                    data?.imageUrl

                if (
                    !imageUrl
                ) {
                    throw new Error(
                        'URL da imagem não retornada.',
                    )
                }

                return imageUrl
            } catch (
            error
            ) {
                console.error(
                    'Erro no upload:',
                    error,
                )

                message.error(
                    error?.response
                        ?.data
                        ?.message ||
                    'Não foi possível enviar a imagem.',
                )

                return null
            } finally {
                setter(false)
            }
        }

    const handleAudioUpload =
        async (
            fieldName,
            file,
        ) => {
            try {
                setUploadingAudio(
                    fieldName,
                )

                const allowedTypes = [
                    'audio/mpeg',
                    'audio/mp3',
                    'audio/wav',
                    'audio/x-wav',
                    'audio/ogg',
                ]

                if (
                    file?.type &&
                    !allowedTypes.includes(
                        file.type,
                    )
                ) {
                    message.error(
                        'Envie um arquivo MP3, WAV ou OGG.',
                    )

                    return false
                }

                const maxSize =
                    15 *
                    1024 *
                    1024

                if (
                    file.size >
                    maxSize
                ) {
                    message.error(
                        'O áudio deve ter no máximo 15 MB.',
                    )

                    return false
                }

                const result =
                    await UploadService.audio(
                        file,
                    )

                const uploadedUrl =
                    result?.url ||
                    result?.data?.url ||
                    result?.path ||
                    result?.data?.path

                if (!uploadedUrl) {
                    throw new Error(
                        'UPLOAD_URL_NOT_FOUND',
                    )
                }

                form.setFieldValue(
                    fieldName,
                    uploadedUrl,
                )

                message.success(
                    'Áudio enviado com sucesso.',
                )
            } catch (error) {
                console.error(
                    'Erro ao enviar áudio:',
                    error,
                )

                message.error(
                    error?.response?.data?.message ||
                    'Não foi possível enviar o áudio.',
                )
            } finally {
                setUploadingAudio(
                    null,
                )
            }

            return false
        }

    const handleCardBackUpload =
        async (file) => {
            const imageUrl =
                await uploadImage(
                    file,
                    setUploadingCardBack,
                )

            if (
                imageUrl
            ) {
                form.setFieldValue(
                    'cardBackImageUrl',
                    imageUrl,
                )

                message.success(
                    'Verso da carta enviado.',
                )
            }

            return false
        }

    const handleStartBackgroundUpload =
        async (file) => {
            const imageUrl =
                await uploadImage(
                    file,
                    setUploadingStartBackground,
                )

            if (
                imageUrl
            ) {
                form.setFieldValue(
                    'startScreenBackgroundUrl',
                    imageUrl,
                )

                message.success(
                    'Fundo da tela inicial enviado.',
                )
            }

            return false
        }

    const handleStartLogoUpload =
        async (file) => {
            const imageUrl =
                await uploadImage(
                    file,
                    setUploadingStartLogo,
                )

            if (
                imageUrl
            ) {
                form.setFieldValue(
                    'startScreenLogoUrl',
                    imageUrl,
                )

                message.success(
                    'Logo da tela inicial enviado.',
                )
            }

            return false
        }

    // ======================================================
    // UPLOAD DO FUNDO DO GAMEPLAY
    // ======================================================

    const handleGameplayBackgroundUpload =
        async (file) => {
            const imageUrl =
                await uploadImage(
                    file,
                    setUploadingGameplayBackground,
                )

            if (
                imageUrl
            ) {
                form.setFieldValue(
                    'gameplayBackgroundUrl',
                    imageUrl,
                )

                message.success(
                    'Fundo do gameplay enviado.',
                )
            }

            return false
        }

    // ======================================================
    // UPLOAD DA LOGO DO GAMEPLAY
    // ======================================================

    const handleGameplayLogoUpload =
        async (file) => {
            const imageUrl =
                await uploadImage(
                    file,
                    setUploadingGameplayLogo,
                )

            if (
                imageUrl
            ) {
                form.setFieldValue(
                    'gameplayLogoUrl',
                    imageUrl,
                )

                message.success(
                    'Logo do gameplay enviada.',
                )
            }

            return false
        }

    // ======================================================
    // RESULTADO - FUNDO
    // ======================================================

    const handleResultBackgroundUpload =
        async (file) => {
            const imageUrl =
                await uploadImage(
                    file,
                    setUploadingResultBackground,
                )

            if (
                imageUrl
            ) {
                form.setFieldValue(
                    'resultBackgroundUrl',
                    imageUrl,
                )

                message.success(
                    'Fundo do resultado enviado.',
                )
            }

            return false
        }

    // ======================================================
    // RESULTADO - LOGO
    // ======================================================

    const handleResultLogoUpload =
        async (file) => {
            const imageUrl =
                await uploadImage(
                    file,
                    setUploadingResultLogo,
                )

            if (
                imageUrl
            ) {
                form.setFieldValue(
                    'resultLogoUrl',
                    imageUrl,
                )

                message.success(
                    'Logo do resultado enviada.',
                )
            }

            return false
        }

    // ======================================================
    // RESULTADO - ARTE DE SUCESSO
    // ======================================================

    const handleResultCompletedImageUpload =
        async (file) => {
            const imageUrl =
                await uploadImage(
                    file,
                    setUploadingResultCompletedImage,
                )

            if (
                imageUrl
            ) {
                form.setFieldValue(
                    'resultCompletedImageUrl',
                    imageUrl,
                )

                message.success(
                    'Arte de sucesso enviada.',
                )
            }

            return false
        }

    // ======================================================
    // RESULTADO - ARTE DE TEMPO ESGOTADO
    // ======================================================

    const handleResultTimeoutImageUpload =
        async (file) => {
            const imageUrl =
                await uploadImage(
                    file,
                    setUploadingResultTimeoutImage,
                )

            if (
                imageUrl
            ) {
                form.setFieldValue(
                    'resultTimeoutImageUrl',
                    imageUrl,
                )

                message.success(
                    'Arte de tempo esgotado enviada.',
                )
            }

            return false
        }

    // ======================================================
    // PREVIEW
    // ======================================================

    const designValues =
        Form.useWatch(
            [],
            form,
        ) || {}

    const previewCardStyle = {
        width:
            designValues.cardWidth ||
            160,

        height:
            designValues.cardSize ||
            160,

        borderRadius:
            designValues.cardBorderRadius ??
            16,

        border:
            `${designValues.cardBorderWidth ?? 2}px solid ${designValues.cardBorderColor || '#e5e7eb'}`,

        boxShadow:
            designValues.cardShadow
                ? '0 10px 30px rgba(0,0,0,.18)'
                : 'none',

        display:
            'flex',

        alignItems:
            'center',

        justifyContent:
            'center',

        overflow:
            'hidden',
    }


    // ======================================================
    // FORMATAR TEMPO
    // ======================================================

    const formatRankingTime =
        (durationMs) => {
            const milliseconds =
                Math.max(
                    0,
                    Number(
                        durationMs ||
                        0,
                    ),
                )

            const totalSeconds =
                milliseconds /
                1000

            if (
                totalSeconds <
                60
            ) {
                return `${totalSeconds.toFixed(
                    1,
                )}s`
            }

            const minutes =
                Math.floor(
                    totalSeconds /
                    60,
                )

            const seconds =
                Math.floor(
                    totalSeconds %
                    60,
                )

            return `${String(
                minutes,
            ).padStart(
                2,
                '0',
            )}:${String(
                seconds,
            ).padStart(
                2,
                '0',
            )}`
        }

    // ======================================================
    // FORMATAR DATA/HORA DA PARTIDA
    // ======================================================

    const formatMatchDate =
        (value) => {
            if (!value) {
                return '-'
            }

            const date =
                new Date(
                    value,
                )

            if (
                Number.isNaN(
                    date.getTime(),
                )
            ) {
                return '-'
            }

            return date.toLocaleString(
                'pt-BR',
            )
        }

    const rankingColumns = [
        {
            title:
                'Posição',

            width:
                90,

            render:
                (
                    _,
                    record,
                    index,
                ) => {
                    const position =
                        index + 1

                    if (
                        position ===
                        1
                    ) {
                        return (
                            <Tag color="gold">
                                1º
                            </Tag>
                        )
                    }

                    if (
                        position ===
                        2
                    ) {
                        return (
                            <Tag>
                                2º
                            </Tag>
                        )
                    }

                    if (
                        position ===
                        3
                    ) {
                        return (
                            <Tag color="orange">
                                3º
                            </Tag>
                        )
                    }

                    return `${position}º`
                },
        },

        {
            title:
                'Participante',

            render:
                (
                    _,
                    record,
                ) => {
                    const participant =
                        record?.participant

                    return (
                        <div>
                            <Text strong>
                                {
                                    participant
                                        ?.name ||
                                    'Participante'
                                }
                            </Text>

                            {participant?.email && (
                                <div>
                                    <Text
                                        type="secondary"
                                        style={{
                                            fontSize:
                                                12,
                                        }}
                                    >
                                        {
                                            participant.email
                                        }
                                    </Text>
                                </div>
                            )}
                        </div>
                    )
                },
        },

        {
            title:
                'Pontuação',

            dataIndex:
                'score',

            width:
                130,

            render:
                (value) => (
                    <Text strong>
                        {
                            Number(
                                value ||
                                0,
                            )
                        }
                    </Text>
                ),
        },

        {
            title:
                'Tempo',

            dataIndex:
                'durationMs',

            width:
                120,

            render:
                (value) =>
                    formatRankingTime(
                        value,
                    ),
        },

        {
            title:
                'Tentativas',

            dataIndex:
                'attempts',

            width:
                120,

            render:
                (value) =>
                    Number(
                        value ||
                        0,
                    ),
        },

        {
            title:
                'Erros',

            dataIndex:
                'errors',

            width:
                100,

            render:
                (value) =>
                    Number(
                        value ||
                        0,
                    ),
        },
    ]

    // ======================================================
    // COLUNAS DO HISTÓRICO
    // ======================================================

    const matchesColumns = [
        {
            title:
                'Data/Hora',

            dataIndex:
                'startedAt',

            width:
                170,

            render:
                (value) =>
                    formatMatchDate(
                        value,
                    ),
        },

        {
            title:
                'Participante',

            render:
                (
                    _,
                    record,
                ) => (
                    <div>
                        <Text strong>
                            {
                                record
                                    ?.participant
                                    ?.name ||
                                'Participante'
                            }
                        </Text>

                        {record
                            ?.participant
                            ?.email && (
                                <div>
                                    <Text
                                        type="secondary"
                                        style={{
                                            fontSize:
                                                12,
                                        }}
                                    >
                                        {
                                            record
                                                .participant
                                                .email
                                        }
                                    </Text>
                                </div>
                            )}
                    </div>
                ),
        },

        {
            title:
                'Status',

            dataIndex:
                'completed',

            width:
                130,

            render:
                (value) =>
                    value ? (
                        <Tag color="green">
                            Concluída
                        </Tag>
                    ) : (
                        <Tag color="red">
                            Não concluída
                        </Tag>
                    ),
        },

        {
            title:
                'Pontuação',

            dataIndex:
                'score',

            width:
                120,

            render:
                (value) =>
                    Number(
                        value ||
                        0,
                    ),
        },

        {
            title:
                'Tempo',

            dataIndex:
                'durationMs',

            width:
                110,

            render:
                (value) =>
                    formatRankingTime(
                        value,
                    ),
        },

        {
            title:
                'Tentativas',

            dataIndex:
                'attempts',

            width:
                110,

            render:
                (value) =>
                    Number(
                        value ||
                        0,
                    ),
        },

        {
            title:
                'Erros',

            dataIndex:
                'errors',

            width:
                90,

            render:
                (value) =>
                    Number(
                        value ||
                        0,
                    ),
        },
    ]

    // ======================================================
    // FILTRAR HISTÓRICO
    // ======================================================

    const filteredMatches =
        useMemo(
            () => {
                const search =
                    String(
                        historySearch ||
                        '',
                    )
                        .trim()
                        .toLowerCase()

                return matches.filter(
                    (item) => {
                        // ==================================
                        // PARTICIPANTE
                        // ==================================

                        if (search) {
                            const participant =
                                item?.participant ||
                                {}

                            const searchableText = [
                                participant.name,
                                participant.email,
                                participant.phone,
                                participant.cpf,
                            ]
                                .filter(
                                    Boolean,
                                )
                                .join(' ')
                                .toLowerCase()

                            if (
                                !searchableText.includes(
                                    search,
                                )
                            ) {
                                return false
                            }
                        }

                        // ==================================
                        // STATUS
                        // ==================================

                        if (
                            historyStatus ===
                            'COMPLETED' &&
                            item?.completed !==
                            true
                        ) {
                            return false
                        }

                        if (
                            historyStatus ===
                            'NOT_COMPLETED' &&
                            item?.completed ===
                            true
                        ) {
                            return false
                        }

                        // ==================================
                        // DATA
                        // ==================================

                        if (
                            historyDate &&
                            item?.startedAt
                        ) {
                            const itemDate =
                                new Date(
                                    item.startedAt,
                                )

                            if (
                                Number.isNaN(
                                    itemDate.getTime(),
                                )
                            ) {
                                return false
                            }

                            const selectedYear =
                                historyDate.year()

                            const selectedMonth =
                                historyDate.month()

                            const selectedDay =
                                historyDate.date()

                            if (
                                itemDate.getFullYear() !==
                                selectedYear ||
                                itemDate.getMonth() !==
                                selectedMonth ||
                                itemDate.getDate() !==
                                selectedDay
                            ) {
                                return false
                            }
                        }

                        return true
                    },
                )
            },
            [
                matches,
                historySearch,
                historyStatus,
                historyDate,
            ],
        )

    return (
        <>
            <div
                style={{
                    marginBottom:
                        24,
                }}
            >
                <Button
                    type="link"
                    style={{
                        padding: 0,
                        marginBottom:
                            8,
                    }}
                    onClick={() =>
                        navigate(
                            '/app/games',
                        )
                    }
                >
                    ← Voltar para Jogos
                </Button>

                <Title
                    level={2}
                    style={{
                        marginBottom:
                            4,
                    }}
                >
                    {selectedGame?.name ||
                        'Jogo da Memória'}
                </Title>

                <Space wrap>
                    <Tag color="blue">
                        Jogo da Memória
                    </Tag>

                    {selectedGame?.status && (
                        <Tag
                            color={
                                selectedGame.status ===
                                    'ACTIVE'
                                    ? 'green'
                                    : 'default'
                            }
                        >
                            {
                                selectedGame.status
                            }
                        </Tag>
                    )}

                    {selectedGame?.event
                        ?.name && (
                            <Text type="secondary">
                                Evento:{' '}
                                {
                                    selectedGame
                                        .event
                                        .name
                                }
                            </Text>
                        )}
                </Space>
            </div>

            {!urlGameId && (
                <Card>
                    <Row
                        gutter={[
                            16,
                            16,
                        ]}
                        align="bottom"
                    >
                        <Col
                            xs={24}
                            md={18}
                        >
                            <Text strong>
                                Jogo
                            </Text>

                            <Select
                                style={{
                                    width:
                                        '100%',
                                    marginTop:
                                        8,
                                }}
                                placeholder="Selecione um jogo da memória"
                                loading={
                                    loadingGames
                                }
                                value={
                                    selectedGameId
                                }
                                onChange={(
                                    value,
                                ) =>
                                    setSelectedGameId(
                                        value,
                                    )
                                }
                                options={games.map(
                                    (
                                        game,
                                    ) => ({
                                        value:
                                            game.id,

                                        label:
                                            game.name ||
                                            game.slug,
                                    }),
                                )}
                            />
                        </Col>

                        <Col
                            xs={24}
                            md={6}
                        >
                            <Button
                                block
                                icon={
                                    <ReloadOutlined />
                                }
                                loading={
                                    loadingGames ||
                                    loadingConfig
                                }
                                onClick={() => {
                                    loadGames()

                                    if (
                                        selectedGameId
                                    ) {
                                        loadConfig(
                                            selectedGameId,
                                        )
                                    }
                                }}
                            >
                                Atualizar
                            </Button>
                        </Col>
                    </Row>
                </Card>
            )}

            <Tabs
                defaultActiveKey="general"
                style={{
                    marginTop: 16,
                }}
            >
                {/* ================================================== */}
                {/* GERAL */}
                {/* ================================================== */}

                <TabPane
                    tab="Geral"
                    key="general"
                >
                    <Card
                        loading={
                            loadingConfig
                        }
                        title="Configuração do jogo"
                    >
                        <Form
                            form={form}
                            layout="vertical"
                        >
                            <Row
                                gutter={[
                                    16,
                                    8,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    sm={12}
                                    lg={8}
                                >
                                    <Form.Item
                                        label="Quantidade de cartas no jogo"
                                        name="pairCount"
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Selecione a quantidade de cartas.',
                                            },
                                        ]}
                                    >
                                        <Select
                                            options={[
                                                {
                                                    value: 4,
                                                    label:
                                                        '8 cartas — 4 pares',
                                                },
                                                {
                                                    value: 8,
                                                    label:
                                                        '16 cartas — 8 pares',
                                                },
                                                {
                                                    value: 12,
                                                    label:
                                                        '24 cartas — 12 pares',
                                                },
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    sm={12}
                                    lg={8}
                                >
                                    <Form.Item
                                        label="Tempo limite (segundos)"
                                        name="timeLimit"
                                    >
                                        <InputNumber
                                            min={
                                                10
                                            }
                                            max={
                                                600
                                            }
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    sm={12}
                                    lg={8}
                                >
                                    <Form.Item
                                        label="Tempo para virar novamente (ms)"
                                        name="revealDuration"
                                    >
                                        <InputNumber
                                            min={
                                                200
                                            }
                                            max={
                                                5000
                                            }
                                            step={
                                                100
                                            }
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <Title
                                level={5}
                                style={{
                                    marginBottom: 16,
                                }}
                            >
                                Memorização inicial
                            </Title>

                            <Row
                                gutter={[
                                    16,
                                    8,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Mostrar cartas no início"
                                        name="revealAtStart"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Tempo de memorização (ms)"
                                        name="revealStartDuration"
                                    >
                                        <InputNumber
                                            min={1000}
                                            max={30000}
                                            step={500}
                                            style={{
                                                width: '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Mostrar contagem"
                                        name="revealCountdownEnabled"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Cronômetro após memorização"
                                        name="timerStartsAfterReveal"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Divider />

                            <Title
                                level={5}
                                style={{
                                    marginBottom:
                                        16,
                                }}
                            >
                                Introdução do jogo
                            </Title>

                            <Row
                                gutter={[
                                    16,
                                    8,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Ativar introdução"
                                        name="introEnabled"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Tempo 'Prepare-se' (ms)"
                                        name="introPrepareDuration"
                                    >
                                        <InputNumber
                                            min={300}
                                            max={10000}
                                            step={100}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Tempo 'Valendo' (ms)"
                                        name="introGoDuration"
                                    >
                                        <InputNumber
                                            min={300}
                                            max={10000}
                                            step={100}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row
                                gutter={[
                                    16,
                                    8,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    md={8}
                                >
                                    <Form.Item
                                        label="Texto Prepare-se"
                                        name="introPrepareText"
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={8}
                                >
                                    <Form.Item
                                        label="Texto Memorização"
                                        name="introMemorizeText"
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={8}
                                >
                                    <Form.Item
                                        label="Texto Valendo"
                                        name="introGoText"
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <Row
                                gutter={[
                                    24,
                                    8,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    sm={12}
                                    lg={6}
                                >
                                    <Form.Item
                                        label="Mostrar cronômetro"
                                        name="showTimer"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    sm={12}
                                    lg={6}
                                >
                                    <Form.Item
                                        label="Mostrar tentativas"
                                        name="showAttempts"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    sm={12}
                                    lg={6}
                                >
                                    <Form.Item
                                        label="Mostrar pontuação"
                                        name="showScore"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    sm={12}
                                    lg={6}
                                >
                                    <Form.Item
                                        label="Mostrar ranking"
                                        name="showRanking"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <Title
                                level={5}
                                style={{
                                    marginBottom: 16,
                                }}
                            >
                                Pontuação
                            </Title>

                            <Row
                                gutter={[
                                    16,
                                    8,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Modo de pontuação"
                                        name="scoreMode"
                                    >
                                        <Select
                                            options={[
                                                {
                                                    value:
                                                        'TIME_ERRORS',
                                                    label:
                                                        'Tempo + erros',
                                                },
                                                {
                                                    value:
                                                        'MATCH_POINTS',
                                                    label:
                                                        'Pontos por par',
                                                },
                                                {
                                                    value:
                                                        'MATCH_COMBO',
                                                    label:
                                                        'Pontos + combo',
                                                },
                                                {
                                                    value:
                                                        'HYBRID',
                                                    label:
                                                        'Híbrido',
                                                },
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Pontos por par"
                                        name="pointsPerMatch"
                                    >
                                        <InputNumber
                                            min={0}
                                            max={100000}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Penalidade por erro"
                                        name="errorPenalty"
                                    >
                                        <InputNumber
                                            min={0}
                                            max={100000}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Penalidade por segundo"
                                        name="timePenaltyPerSec"
                                    >
                                        <InputNumber
                                            min={0}
                                            max={100000}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row>
                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Combo afeta a pontuação"
                                        name="comboAffectsScore"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Button
                                type="primary"
                                icon={
                                    <SaveOutlined />
                                }
                                loading={
                                    savingConfig
                                }
                                onClick={
                                    handleSaveConfig
                                }
                            >
                                Salvar configuração
                            </Button>
                        </Form>
                    </Card>
                </TabPane>

                {/* ================================================== */}
                {/* TELA INICIAL */}
                {/* ================================================== */}

                <TabPane
                    tab="Tela Inicial"
                    key="start-screen"
                >
                    <Card
                        title="Personalização da tela inicial"
                        loading={
                            loadingConfig
                        }
                    >
                        <Form
                            form={
                                form
                            }
                            layout="vertical"
                        >
                            <Row
                                gutter={[
                                    16,
                                    8,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Ativar tela inicial"
                                        name="startScreenEnabled"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <Title
                                level={5}
                            >
                                Fundo
                            </Title>

                            <Row
                                gutter={[
                                    16,
                                    16,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    md={8}
                                >
                                    <Form.Item
                                        label="Cor de fundo"
                                        name="startScreenBackgroundColor"
                                    >
                                        <Input
                                            type="color"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={16}
                                >
                                    <Form.Item
                                        label="Imagem de fundo"
                                    >
                                        <Form.Item
                                            name="startScreenBackgroundUrl"
                                            noStyle
                                        >
                                            <Input
                                                type="hidden"
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            shouldUpdate
                                            noStyle
                                        >
                                            {() => {
                                                const value =
                                                    form.getFieldValue(
                                                        'startScreenBackgroundUrl',
                                                    )

                                                return (
                                                    <Space
                                                        direction="vertical"
                                                    >
                                                        {value && (
                                                            <Image
                                                                src={
                                                                    getImageUrl(
                                                                        value,
                                                                    )
                                                                }
                                                                width={
                                                                    220
                                                                }
                                                            />
                                                        )}

                                                        <Upload
                                                            accept="image/*"
                                                            showUploadList={
                                                                false
                                                            }
                                                            beforeUpload={
                                                                handleStartBackgroundUpload
                                                            }
                                                        >
                                                            <Button
                                                                icon={
                                                                    <UploadOutlined />
                                                                }
                                                                loading={
                                                                    uploadingStartBackground
                                                                }
                                                            >
                                                                {value
                                                                    ? 'Trocar fundo'
                                                                    : 'Enviar fundo'}
                                                            </Button>
                                                        </Upload>

                                                        {value && (
                                                            <Button
                                                                danger
                                                                type="link"
                                                                onClick={() =>
                                                                    form.setFieldValue(
                                                                        'startScreenBackgroundUrl',
                                                                        '',
                                                                    )
                                                                }
                                                            >
                                                                Remover fundo
                                                            </Button>
                                                        )}
                                                    </Space>
                                                )
                                            }}
                                        </Form.Item>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <Title
                                level={5}
                            >
                                Logo
                            </Title>

                            <Row
                                gutter={[
                                    16,
                                    16,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Mostrar logo"
                                        name="startScreenShowLogo"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Largura da logo"
                                        name="startScreenLogoWidth"
                                    >
                                        <InputNumber
                                            min={40}
                                            max={800}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Altura máxima da logo"
                                        name="startScreenLogoHeight"
                                    >
                                        <InputNumber
                                            min={40}
                                            max={800}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Arquivo da logo"
                                    >
                                        <Form.Item
                                            name="startScreenLogoUrl"
                                            noStyle
                                        >
                                            <Input
                                                type="hidden"
                                            />
                                        </Form.Item>

                                        <Upload
                                            accept="image/*"
                                            showUploadList={
                                                false
                                            }
                                            beforeUpload={
                                                handleStartLogoUpload
                                            }
                                        >
                                            <Button
                                                icon={
                                                    <UploadOutlined />
                                                }
                                                loading={
                                                    uploadingStartLogo
                                                }
                                            >
                                                Enviar logo
                                            </Button>
                                        </Upload>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <Title
                                level={5}
                            >
                                Textos
                            </Title>

                            <Row
                                gutter={[
                                    16,
                                    8,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    md={12}
                                >
                                    <Form.Item
                                        label="Título"
                                        name="startScreenTitle"
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Mostrar título"
                                        name="startScreenShowTitle"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={3}
                                >
                                    <Form.Item
                                        label="Cor"
                                        name="startScreenTitleColor"
                                    >
                                        <Input
                                            type="color"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={3}
                                >
                                    <Form.Item
                                        label="Tamanho"
                                        name="startScreenTitleFontSize"
                                    >
                                        <InputNumber
                                            min={12}
                                            max={180}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row
                                gutter={[
                                    16,
                                    8,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    md={12}
                                >
                                    <Form.Item
                                        label="Subtítulo"
                                        name="startScreenSubtitle"
                                    >
                                        <Input.TextArea
                                            rows={3}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Mostrar subtítulo"
                                        name="startScreenShowSubtitle"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={3}
                                >
                                    <Form.Item
                                        label="Cor"
                                        name="startScreenSubtitleColor"
                                    >
                                        <Input
                                            type="color"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={3}
                                >
                                    <Form.Item
                                        label="Tamanho"
                                        name="startScreenSubtitleFontSize"
                                    >
                                        <InputNumber
                                            min={12}
                                            max={100}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <Title
                                level={5}
                            >
                                Botão Jogar
                            </Title>

                            <Row
                                gutter={[
                                    16,
                                    8,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Texto"
                                        name="startScreenButtonText"
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Cor do botão"
                                        name="startScreenPrimaryColor"
                                    >
                                        <Input
                                            type="color"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Cor do texto"
                                        name="startScreenButtonTextColor"
                                    >
                                        <Input
                                            type="color"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Tamanho do botão"
                                        name="startScreenPlayButtonSize"
                                    >
                                        <InputNumber
                                            min={60}
                                            max={400}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <Title
                                level={5}
                            >
                                Texto auxiliar
                            </Title>

                            <Row
                                gutter={[
                                    16,
                                    8,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    md={12}
                                >
                                    <Form.Item
                                        label="Texto"
                                        name="startScreenHintText"
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={4}
                                >
                                    <Form.Item
                                        label="Mostrar"
                                        name="startScreenShowHint"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={4}
                                >
                                    <Form.Item
                                        label="Cor"
                                        name="startScreenHintColor"
                                    >
                                        <Input
                                            type="color"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={4}
                                >
                                    <Form.Item
                                        label="Tamanho"
                                        name="startScreenHintFontSize"
                                    >
                                        <InputNumber
                                            min={10}
                                            max={80}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Button
                                type="primary"
                                icon={
                                    <SaveOutlined />
                                }
                                loading={
                                    savingConfig
                                }
                                onClick={
                                    handleSaveConfig
                                }
                            >
                                Salvar tela inicial
                            </Button>
                        </Form>
                    </Card>
                </TabPane>

                {/* ================================================== */}
                {/* FORMULÁRIO */}
                {/* ================================================== */}

                <TabPane
                    tab="Formulário"
                    key="form"
                >
                    <Card title="Personalização do formulário">
                        <Text type="secondary">
                            Aqui configuraremos a aparência da tela de captação de leads.
                        </Text>
                    </Card>
                </TabPane>

                {/* ================================================== */}
                {/* JOGO */}
                {/* ================================================== */}

                <TabPane
                    tab="Jogo"
                    key="game"
                >
                    <Form
                        form={form}
                        layout="vertical"
                    >
                        <Title
                            level={5}
                            style={{
                                marginBottom: 20,
                            }}
                        >
                            Tela do jogo
                        </Title>

                        {/* ================================================== */}
                        {/* TELA E ORIENTAÇÃO */}
                        {/* ================================================== */}

                        <Row
                            gutter={[20, 8]}
                        >
                            <Col
                                xs={24}
                                md={8}
                            >
                                <Form.Item
                                    label="Orientação"
                                    name="layoutOrientation"
                                >
                                    <Select
                                        options={[
                                            {
                                                value: 'AUTO',
                                                label: 'Automática',
                                            },
                                            {
                                                value: 'PORTRAIT',
                                                label: 'Vertical 1080 × 1920',
                                            },
                                            {
                                                value: 'LANDSCAPE',
                                                label: 'Horizontal 1920 × 1080',
                                            },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={8}
                            >
                                <Form.Item
                                    label="Colunas no vertical"
                                    name="portraitColumns"
                                >
                                    <InputNumber
                                        min={1}
                                        max={12}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={8}
                            >
                                <Form.Item
                                    label="Colunas no horizontal"
                                    name="landscapeColumns"
                                >
                                    <InputNumber
                                        min={1}
                                        max={12}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider />

                        {/* ================================================== */}
                        {/* FUNDO */}
                        {/* ================================================== */}

                        <Title
                            level={5}
                            style={{
                                marginBottom: 20,
                            }}
                        >
                            Fundo do gameplay
                        </Title>

                        <Row
                            gutter={[20, 8]}
                        >
                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Cor de fundo"
                                    name="gameplayBackgroundColor"
                                >
                                    <Input
                                        type="color"
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Cor principal"
                                    name="gameplayPrimaryColor"
                                >
                                    <Input
                                        type="color"
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Cor dos textos"
                                    name="gameplayTextColor"
                                >
                                    <Input
                                        type="color"
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Ajuste da imagem de fundo"
                                    name="backgroundFit"
                                >
                                    <Select
                                        options={[
                                            {
                                                value: 'cover',
                                                label: 'Preencher',
                                            },
                                            {
                                                value: 'contain',
                                                label: 'Conter',
                                            },
                                            {
                                                value: 'fill',
                                                label: 'Esticar',
                                            },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            label="Imagem de fundo do gameplay"
                        >
                            <Form.Item
                                name="gameplayBackgroundUrl"
                                noStyle
                            >
                                <Input
                                    type="hidden"
                                />
                            </Form.Item>

                            <Form.Item
                                shouldUpdate
                                noStyle
                            >
                                {() => {
                                    const value =
                                        form.getFieldValue(
                                            'gameplayBackgroundUrl',
                                        )

                                    return (
                                        <Space
                                            direction="vertical"
                                            size={12}
                                        >
                                            {value && (
                                                <Image
                                                    src={getImageUrl(
                                                        value,
                                                    )}
                                                    width={320}
                                                    style={{
                                                        maxHeight: 180,
                                                        objectFit: 'cover',
                                                        borderRadius: 10,
                                                    }}
                                                />
                                            )}

                                            <Space
                                                wrap
                                            >
                                                <Upload
                                                    accept="image/*"
                                                    showUploadList={false}
                                                    beforeUpload={
                                                        handleGameplayBackgroundUpload
                                                    }
                                                >
                                                    <Button
                                                        icon={
                                                            <UploadOutlined />
                                                        }
                                                        loading={
                                                            uploadingGameplayBackground
                                                        }
                                                    >
                                                        {value
                                                            ? 'Trocar fundo'
                                                            : 'Enviar fundo'}
                                                    </Button>
                                                </Upload>

                                                {value && (
                                                    <Button
                                                        danger
                                                        onClick={() =>
                                                            form.setFieldValue(
                                                                'gameplayBackgroundUrl',
                                                                '',
                                                            )
                                                        }
                                                    >
                                                        Remover fundo
                                                    </Button>
                                                )}
                                            </Space>
                                        </Space>
                                    )
                                }}
                            </Form.Item>
                        </Form.Item>

                        <Divider />

                        {/* ================================================== */}
                        {/* LOGO */}
                        {/* ================================================== */}

                        <Title
                            level={5}
                            style={{
                                marginBottom: 20,
                            }}
                        >
                            Logo no gameplay
                        </Title>

                        <Row
                            gutter={[20, 8]}
                            align="bottom"
                        >
                            <Col
                                xs={24}
                                lg={10}
                            >
                                <Form.Item
                                    label="Logo do gameplay"
                                >
                                    <Form.Item
                                        name="gameplayLogoUrl"
                                        noStyle
                                    >
                                        <Input
                                            type="hidden"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        shouldUpdate
                                        noStyle
                                    >
                                        {() => {
                                            const value =
                                                form.getFieldValue(
                                                    'gameplayLogoUrl',
                                                )

                                            return (
                                                <Space
                                                    direction="vertical"
                                                    size={12}
                                                >
                                                    {value && (
                                                        <Image
                                                            src={getImageUrl(
                                                                value,
                                                            )}
                                                            width={180}
                                                            style={{
                                                                maxHeight: 100,
                                                                objectFit:
                                                                    'contain',
                                                                background:
                                                                    '#fafafa',
                                                                padding: 8,
                                                                borderRadius:
                                                                    8,
                                                            }}
                                                        />
                                                    )}

                                                    <Space wrap>
                                                        <Upload
                                                            accept="image/*"
                                                            showUploadList={
                                                                false
                                                            }
                                                            beforeUpload={
                                                                handleGameplayLogoUpload
                                                            }
                                                        >
                                                            <Button
                                                                icon={
                                                                    <UploadOutlined />
                                                                }
                                                                loading={
                                                                    uploadingGameplayLogo
                                                                }
                                                            >
                                                                {value
                                                                    ? 'Trocar logo'
                                                                    : 'Enviar logo'}
                                                            </Button>
                                                        </Upload>

                                                        {value && (
                                                            <Button
                                                                danger
                                                                onClick={() =>
                                                                    form.setFieldValue(
                                                                        'gameplayLogoUrl',
                                                                        '',
                                                                    )
                                                                }
                                                            >
                                                                Remover logo
                                                            </Button>
                                                        )}
                                                    </Space>
                                                </Space>
                                            )
                                        }}
                                    </Form.Item>
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={8}
                                lg={4}
                            >
                                <Form.Item
                                    label="Mostrar logo"
                                    name="showGameLogo"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={8}
                                lg={5}
                            >
                                <Form.Item
                                    label="Largura da logo (px)"
                                    name="gameLogoWidth"
                                >
                                    <InputNumber
                                        min={40}
                                        max={600}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={8}
                                lg={5}
                            >
                                <Form.Item
                                    label="Altura máxima (px)"
                                    name="gameLogoHeight"
                                >
                                    <InputNumber
                                        min={40}
                                        max={400}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider />

                        {/* ================================================== */}
                        {/* PAINEL DE INFORMAÇÕES */}
                        {/* ================================================== */}

                        <Title
                            level={5}
                            style={{
                                marginBottom: 20,
                            }}
                        >
                            Painel de informações
                        </Title>

                        <Row
                            gutter={[20, 8]}
                        >
                            <Col
                                xs={12}
                                md={6}
                            >
                                <Form.Item
                                    label="Mostrar tempo"
                                    name="showTimer"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={12}
                                md={6}
                            >
                                <Form.Item
                                    label="Mostrar tentativas"
                                    name="showAttempts"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={12}
                                md={6}
                            >
                                <Form.Item
                                    label="Mostrar pontuação"
                                    name="showScore"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={12}
                                md={6}
                            >
                                <Form.Item
                                    label="Sombra do painel"
                                    name="hudShadow"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row
                            gutter={[20, 8]}
                        >
                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Fundo do painel"
                                    name="hudBackground"
                                >
                                    <Input
                                        placeholder="Ex.: #ffffff"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Cor dos valores"
                                    name="hudTextColor"
                                >
                                    <Input
                                        type="color"
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Cor dos títulos"
                                    name="hudLabelColor"
                                >
                                    <Input
                                        type="color"
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Arredondamento (px)"
                                    name="hudBorderRadius"
                                >
                                    <InputNumber
                                        min={0}
                                        max={999}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider />

                        {/* ================================================== */}
                        {/* CARTAS */}
                        {/* ================================================== */}

                        <Title
                            level={5}
                            style={{
                                marginBottom: 20,
                            }}
                        >
                            Cartas e tabuleiro
                        </Title>

                        <Row
                            gutter={[20, 8]}
                        >
                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Largura da carta (px)"
                                    name="cardWidth"
                                >
                                    <InputNumber
                                        min={40}
                                        max={1000}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Altura da carta (px)"
                                    name="cardSize"
                                >
                                    <InputNumber
                                        min={60}
                                        max={1000}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Espaçamento entre cartas (px)"
                                    name="cardGap"
                                >
                                    <InputNumber
                                        min={0}
                                        max={100}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Largura máxima do tabuleiro (px)"
                                    name="boardMaxWidth"
                                >
                                    <InputNumber
                                        min={300}
                                        max={3000}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row
                            gutter={[20, 8]}
                        >
                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Arredondamento da carta"
                                    name="cardBorderRadius"
                                >
                                    <InputNumber
                                        min={0}
                                        max={100}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Espessura da borda"
                                    name="cardBorderWidth"
                                >
                                    <InputNumber
                                        min={0}
                                        max={20}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Ajuste da imagem"
                                    name="cardImageFit"
                                >
                                    <Select
                                        options={[
                                            {
                                                value: 'fill',
                                                label: 'Preencher exatamente',
                                            },
                                            {
                                                value: 'cover',
                                                label: 'Cobrir mantendo proporção',
                                            },
                                            {
                                                value: 'contain',
                                                label: 'Conter mantendo proporção',
                                            },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Sombra nas cartas"
                                    name="cardShadow"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider />

                        {/* ================================================== */}
                        {/* EFEITO DE ACERTO */}
                        {/* ================================================== */}

                        <Title
                            level={5}
                            style={{
                                marginBottom: 20,
                            }}
                        >
                            Efeito ao encontrar um par
                        </Title>

                        <Row
                            gutter={[20, 8]}
                        >
                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Efeito de acerto"
                                    name="matchEffectEnabled"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Duração do efeito (ms)"
                                    name="matchEffectDuration"
                                >
                                    <InputNumber
                                        min={100}
                                        max={5000}
                                        step={50}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Cor do brilho"
                                    name="matchGlowColor"
                                >
                                    <Input
                                        type="color"
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Após encontrar o par"
                                    name="matchedCardBehavior"
                                >
                                    <Select
                                        options={[
                                            {
                                                value: 'KEEP',
                                                label: 'Manter aberto',
                                            },
                                            {
                                                value: 'DIM',
                                                label: 'Deixar translúcido',
                                            },
                                            {
                                                value: 'HIDE',
                                                label: 'Desaparecer',
                                            },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            noStyle
                            shouldUpdate={(
                                previous,
                                current,
                            ) =>
                                previous.matchedCardBehavior !==
                                current.matchedCardBehavior
                            }
                        >
                            {({
                                getFieldValue,
                            }) =>
                                getFieldValue(
                                    'matchedCardBehavior',
                                ) === 'DIM' ? (
                                    <Row
                                        gutter={[20, 8]}
                                    >
                                        <Col
                                            xs={24}
                                            sm={12}
                                            lg={6}
                                        >
                                            <Form.Item
                                                label="Opacidade após acerto (%)"
                                                name="matchedCardOpacity"
                                            >
                                                <InputNumber
                                                    min={0}
                                                    max={100}
                                                    style={{
                                                        width: '100%',
                                                    }}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                ) : null
                            }
                        </Form.Item>

                        <Divider />

                        {/* ================================================== */}
                        {/* COMBO */}
                        {/* ================================================== */}

                        <Title
                            level={5}
                            style={{
                                marginBottom: 20,
                            }}
                        >
                            Combo de acertos
                        </Title>

                        <Row
                            gutter={[20, 8]}
                        >
                            <Col
                                xs={24}
                                sm={12}
                                lg={5}
                            >
                                <Form.Item
                                    label="Ativar combo"
                                    name="comboEnabled"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={5}
                            >
                                <Form.Item
                                    label="Bônus por sequência"
                                    name="comboBaseMultiplier"
                                >
                                    <InputNumber
                                        min={0}
                                        max={10}
                                        step={0.1}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={5}
                            >
                                <Form.Item
                                    label="Multiplicador máximo"
                                    name="comboMaxMultiplier"
                                >
                                    <InputNumber
                                        min={1}
                                        max={20}
                                        step={0.5}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={5}
                            >
                                <Form.Item
                                    label="Duração na tela (ms)"
                                    name="comboDisplayDuration"
                                >
                                    <InputNumber
                                        min={200}
                                        max={5000}
                                        step={100}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={4}
                            >
                                <Form.Item
                                    label="Cor do combo"
                                    name="comboColor"
                                >
                                    <Input
                                        type="color"
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider />

                        <Button
                            type="primary"
                            size="large"
                            icon={
                                <SaveOutlined />
                            }
                            loading={
                                savingConfig
                            }
                            onClick={
                                handleSaveConfig
                            }
                        >
                            Salvar Jogo
                        </Button>

                    </Form>
                </TabPane>

                {/* ================================================== */}
                {/* ABA PREMIAÇÃO */}
                {/* ================================================== */}

                <TabPane
                    tab="Premiação"
                    key="prizes"
                >
                    <Form
                        form={form}
                        layout="vertical"
                    >
                        <div
                            style={{
                                maxWidth:
                                    1000,
                            }}
                        >
                            <Title
                                level={4}
                                style={{
                                    marginBottom:
                                        6,
                                }}
                            >
                                Premiação
                            </Title>

                            <Text
                                type="secondary"
                            >
                                Configure a entrega de brindes de acordo com o desempenho do participante.
                            </Text>

                            <Divider />

                            <Row
                                gutter={[
                                    24,
                                    24,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    lg={12}
                                >
                                    <Form.Item
                                        name="prizeEnabled"
                                        label="Ativar premiação"
                                        valuePropName="checked"
                                        tooltip="Quando ativado, o jogo poderá entregar um brinde ao final da partida."
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    lg={12}
                                >
                                    <Form.Item
                                        noStyle
                                        shouldUpdate={(
                                            previousValues,
                                            currentValues,
                                        ) =>
                                            previousValues.prizeEnabled !==
                                            currentValues.prizeEnabled
                                        }
                                    >
                                        {({
                                            getFieldValue,
                                        }) => {
                                            const enabled =
                                                getFieldValue(
                                                    'prizeEnabled',
                                                )

                                            return (
                                                <Form.Item
                                                    name="prizeRuleType"
                                                    label="Critério de premiação"
                                                >
                                                    <Select
                                                        disabled={
                                                            !enabled
                                                        }
                                                        options={[
                                                            {
                                                                value:
                                                                    'SCORE',
                                                                label:
                                                                    'Por pontuação',
                                                            },
                                                            {
                                                                value:
                                                                    'ATTEMPTS',
                                                                label:
                                                                    'Por tentativas',
                                                            },
                                                            {
                                                                value:
                                                                    'TIME',
                                                                label:
                                                                    'Por tempo',
                                                            },
                                                            {
                                                                value:
                                                                    'HYBRID',
                                                                label:
                                                                    'Pontuação + tentativas + tempo',
                                                            },
                                                            {
                                                                value:
                                                                    'RANDOM_ELIGIBLE',
                                                                label:
                                                                    'Aleatório entre brindes elegíveis',
                                                            },
                                                        ]}
                                                    />
                                                </Form.Item>
                                            )
                                        }}
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Alert
                                type="info"
                                showIcon
                                message="Como funciona"
                                description={
                                    <>
                                        O jogo verifica as regras configuradas ao final da partida.
                                        O participante recebe o brinde correspondente à melhor regra
                                        atendida, desde que exista estoque disponível.
                                    </>
                                }
                                style={{
                                    marginBottom:
                                        24,
                                }}
                            />

                            <Button
                                type="primary"
                                icon={
                                    <SaveOutlined />
                                }
                                loading={
                                    savingConfig
                                }
                                onClick={
                                    handleSaveConfig
                                }
                            >
                                Salvar configuração de premiação
                            </Button>
                            <Divider />

                            <Card
                                title="Regras de premiação"
                                bordered
                                extra={
                                    <Button
                                        type="primary"
                                        onClick={
                                            handleNewPrizeRule
                                        }
                                    >
                                        + Nova regra
                                    </Button>
                                }
                            >
                                <Table
                                    rowKey="id"
                                    loading={
                                        loadingPrizeRules
                                    }
                                    dataSource={
                                        prizeRules
                                    }
                                    columns={[
                                        {
                                            title:
                                                'Prioridade',

                                            dataIndex:
                                                'priority',

                                            width:
                                                100,
                                        },

                                        {
                                            title:
                                                'Brinde',

                                            render:
                                                (
                                                    _,
                                                    record,
                                                ) => (
                                                    <Space>
                                                        {record?.prize
                                                            ?.imageUrl && (
                                                                <Image
                                                                    src={
                                                                        getImageUrl(
                                                                            record.prize.imageUrl,
                                                                        )
                                                                    }
                                                                    width={
                                                                        44
                                                                    }
                                                                    height={
                                                                        44
                                                                    }
                                                                    preview={
                                                                        false
                                                                    }
                                                                    style={{
                                                                        objectFit:
                                                                            'contain',
                                                                        borderRadius:
                                                                            6,
                                                                    }}
                                                                />
                                                            )}

                                                        <div>
                                                            <Text
                                                                strong
                                                            >
                                                                {
                                                                    record
                                                                        ?.prize
                                                                        ?.name ||
                                                                    'Brinde'
                                                                }
                                                            </Text>

                                                            {record
                                                                ?.prize
                                                                ?.stockAvailable !==
                                                                undefined && (
                                                                    <div>
                                                                        <Text
                                                                            type="secondary"
                                                                            style={{
                                                                                fontSize:
                                                                                    12,
                                                                            }}
                                                                        >
                                                                            Estoque:{' '}
                                                                            {
                                                                                record
                                                                                    .prize
                                                                                    .stockAvailable
                                                                            }
                                                                        </Text>
                                                                    </div>
                                                                )}
                                                        </div>
                                                    </Space>
                                                ),
                                        },

                                        {
                                            title:
                                                'Pontuação mín.',

                                            dataIndex:
                                                'minScore',

                                            width:
                                                130,

                                            render:
                                                (value) =>
                                                    value ??
                                                    '-',
                                        },

                                        {
                                            title:
                                                'Tentativas máx.',

                                            dataIndex:
                                                'maxAttempts',

                                            width:
                                                140,

                                            render:
                                                (value) =>
                                                    value ??
                                                    '-',
                                        },

                                        {
                                            title:
                                                'Tempo máx.',

                                            dataIndex:
                                                'maxTimeMs',

                                            width:
                                                130,

                                            render:
                                                (value) =>
                                                    value
                                                        ? `${(
                                                            Number(
                                                                value,
                                                            ) /
                                                            1000
                                                        ).toFixed(
                                                            1,
                                                        )}s`
                                                        : '-',
                                        },

                                        {
                                            title:
                                                'Status',

                                            dataIndex:
                                                'isActive',

                                            width:
                                                100,

                                            render:
                                                (value) =>
                                                    value ? (
                                                        <Tag color="green">
                                                            Ativa
                                                        </Tag>
                                                    ) : (
                                                        <Tag>
                                                            Inativa
                                                        </Tag>
                                                    ),
                                        },

                                        {
                                            title:
                                                'Ações',

                                            width:
                                                180,

                                            render:
                                                (
                                                    _,
                                                    record,
                                                ) => (
                                                    <Space>
                                                        <Button
                                                            size="small"
                                                            onClick={() =>
                                                                handleEditPrizeRule(
                                                                    record,
                                                                )
                                                            }
                                                        >
                                                            Editar
                                                        </Button>

                                                        <Popconfirm
                                                            title="Excluir esta regra?"
                                                            okText="Excluir"
                                                            cancelText="Cancelar"
                                                            okButtonProps={{
                                                                danger:
                                                                    true,
                                                            }}
                                                            onConfirm={() =>
                                                                handleDeletePrizeRule(
                                                                    record.id,
                                                                )
                                                            }
                                                        >
                                                            <Button
                                                                danger
                                                                size="small"
                                                            >
                                                                Excluir
                                                            </Button>
                                                        </Popconfirm>
                                                    </Space>
                                                ),
                                        },
                                    ]}
                                    pagination={
                                        false
                                    }
                                    locale={{
                                        emptyText:
                                            'Nenhuma regra de premiação configurada.',
                                    }}
                                />
                            </Card>
                        </div>
                    </Form>
                </TabPane>

                {/* ================================================== */}
                {/* RESULTADO */}
                {/* ================================================== */}

                <TabPane
                    tab="Resultado"
                    key="result"
                >
                    <Form
                        form={form}
                        layout="vertical"
                    >
                        <Title
                            level={5}
                            style={{
                                marginBottom: 20,
                            }}
                        >
                            Tela de resultado
                        </Title>

                        <Card
                            title="Modo da tela de resultado"
                            style={{
                                marginBottom: 24,
                            }}
                        >
                            <Row
                                gutter={[
                                    20,
                                    16,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    md={12}
                                >
                                    <Form.Item
                                        name="resultImageOnly"
                                        label="Usar somente arte"
                                        valuePropName="checked"
                                        tooltip="Quando ativado, a tela esconderá título, troféu, pontuação, ranking e demais informações."
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={12}
                                >
                                    <Form.Item
                                        name="resultShowBackButton"
                                        label="Mostrar botão voltar"
                                        valuePropName="checked"
                                        tooltip="Mantém apenas o botão de próximo participante sobre a arte."
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <Row
                                gutter={[
                                    24,
                                    24,
                                ]}
                            >
                                {/* ============================================== */}
                                {/* ARTE DE SUCESSO */}
                                {/* ============================================== */}

                                <Col
                                    xs={24}
                                    lg={12}
                                >
                                    <Form.Item
                                        label="Arte quando encontrar todos os pares"
                                    >
                                        <Form.Item
                                            name="resultCompletedImageUrl"
                                            noStyle
                                        >
                                            <Input
                                                type="hidden"
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            shouldUpdate
                                            noStyle
                                        >
                                            {() => {
                                                const value =
                                                    form.getFieldValue(
                                                        'resultCompletedImageUrl',
                                                    )

                                                return (
                                                    <Space
                                                        direction="vertical"
                                                        size={12}
                                                        style={{
                                                            width:
                                                                '100%',
                                                        }}
                                                    >
                                                        {value && (
                                                            <Image
                                                                src={
                                                                    getImageUrl(
                                                                        value,
                                                                    )
                                                                }
                                                                width={260}
                                                                style={{
                                                                    maxHeight:
                                                                        460,

                                                                    objectFit:
                                                                        'contain',

                                                                    borderRadius:
                                                                        10,
                                                                }}
                                                            />
                                                        )}

                                                        <Space
                                                            wrap
                                                        >
                                                            <Upload
                                                                accept="image/*"
                                                                showUploadList={
                                                                    false
                                                                }
                                                                beforeUpload={
                                                                    handleResultCompletedImageUpload
                                                                }
                                                            >
                                                                <Button
                                                                    icon={
                                                                        <UploadOutlined />
                                                                    }
                                                                    loading={
                                                                        uploadingResultCompletedImage
                                                                    }
                                                                >
                                                                    {value
                                                                        ? 'Trocar arte de sucesso'
                                                                        : 'Enviar arte de sucesso'}
                                                                </Button>
                                                            </Upload>

                                                            {value && (
                                                                <Button
                                                                    danger
                                                                    onClick={() =>
                                                                        form.setFieldValue(
                                                                            'resultCompletedImageUrl',
                                                                            '',
                                                                        )
                                                                    }
                                                                >
                                                                    Remover
                                                                </Button>
                                                            )}
                                                        </Space>
                                                    </Space>
                                                )
                                            }}
                                        </Form.Item>
                                    </Form.Item>
                                </Col>

                                {/* ============================================== */}
                                {/* ARTE DE TEMPO ESGOTADO */}
                                {/* ============================================== */}

                                <Col
                                    xs={24}
                                    lg={12}
                                >
                                    <Form.Item
                                        label="Arte quando o tempo esgotar"
                                    >
                                        <Form.Item
                                            name="resultTimeoutImageUrl"
                                            noStyle
                                        >
                                            <Input
                                                type="hidden"
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            shouldUpdate
                                            noStyle
                                        >
                                            {() => {
                                                const value =
                                                    form.getFieldValue(
                                                        'resultTimeoutImageUrl',
                                                    )

                                                return (
                                                    <Space
                                                        direction="vertical"
                                                        size={12}
                                                        style={{
                                                            width:
                                                                '100%',
                                                        }}
                                                    >
                                                        {value && (
                                                            <Image
                                                                src={
                                                                    getImageUrl(
                                                                        value,
                                                                    )
                                                                }
                                                                width={260}
                                                                style={{
                                                                    maxHeight:
                                                                        460,

                                                                    objectFit:
                                                                        'contain',

                                                                    borderRadius:
                                                                        10,
                                                                }}
                                                            />
                                                        )}

                                                        <Space
                                                            wrap
                                                        >
                                                            <Upload
                                                                accept="image/*"
                                                                showUploadList={
                                                                    false
                                                                }
                                                                beforeUpload={
                                                                    handleResultTimeoutImageUpload
                                                                }
                                                            >
                                                                <Button
                                                                    icon={
                                                                        <UploadOutlined />
                                                                    }
                                                                    loading={
                                                                        uploadingResultTimeoutImage
                                                                    }
                                                                >
                                                                    {value
                                                                        ? 'Trocar arte de tempo esgotado'
                                                                        : 'Enviar arte de tempo esgotado'}
                                                                </Button>
                                                            </Upload>

                                                            {value && (
                                                                <Button
                                                                    danger
                                                                    onClick={() =>
                                                                        form.setFieldValue(
                                                                            'resultTimeoutImageUrl',
                                                                            '',
                                                                        )
                                                                    }
                                                                >
                                                                    Remover
                                                                </Button>
                                                            )}
                                                        </Space>
                                                    </Space>
                                                )
                                            }}
                                        </Form.Item>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* ================================================ */}
                        {/* FUNDO */}
                        {/* ================================================ */}

                        <Row
                            gutter={[20, 8]}
                        >
                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Cor de fundo"
                                    name="resultBackgroundColor"
                                >
                                    <Input
                                        type="color"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Cor principal"
                                    name="resultPrimaryColor"
                                >
                                    <Input
                                        type="color"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Cor dos textos"
                                    name="resultTextColor"
                                >
                                    <Input
                                        type="color"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Cor do título"
                                    name="resultTitleColor"
                                >
                                    <Input
                                        type="color"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            label="Imagem de fundo"
                        >
                            <Form.Item
                                name="resultBackgroundUrl"
                                noStyle
                            >
                                <Input
                                    type="hidden"
                                />
                            </Form.Item>

                            <Form.Item
                                shouldUpdate
                                noStyle
                            >
                                {() => {
                                    const value =
                                        form.getFieldValue(
                                            'resultBackgroundUrl',
                                        )

                                    return (
                                        <Space
                                            direction="vertical"
                                            size={12}
                                        >
                                            {value && (
                                                <Image
                                                    src={
                                                        getImageUrl(
                                                            value,
                                                        )
                                                    }
                                                    width={320}
                                                    style={{
                                                        maxHeight: 180,
                                                        objectFit:
                                                            'cover',
                                                        borderRadius:
                                                            10,
                                                    }}
                                                />
                                            )}

                                            <Space wrap>
                                                <Upload
                                                    accept="image/*"
                                                    showUploadList={
                                                        false
                                                    }
                                                    beforeUpload={
                                                        handleResultBackgroundUpload
                                                    }
                                                >
                                                    <Button
                                                        icon={
                                                            <UploadOutlined />
                                                        }
                                                        loading={
                                                            uploadingResultBackground
                                                        }
                                                    >
                                                        {value
                                                            ? 'Trocar fundo'
                                                            : 'Enviar fundo'}
                                                    </Button>
                                                </Upload>

                                                {value && (
                                                    <Button
                                                        danger
                                                        onClick={() =>
                                                            form.setFieldValue(
                                                                'resultBackgroundUrl',
                                                                '',
                                                            )
                                                        }
                                                    >
                                                        Remover
                                                    </Button>
                                                )}
                                            </Space>
                                        </Space>
                                    )
                                }}
                            </Form.Item>
                        </Form.Item>

                        <Divider />

                        {/* ================================================ */}
                        {/* LOGO */}
                        {/* ================================================ */}

                        <Title level={5}>
                            Logo
                        </Title>

                        <Row
                            gutter={[20, 8]}
                            align="bottom"
                        >
                            <Col
                                xs={24}
                                lg={10}
                            >
                                <Form.Item
                                    label="Logo do resultado"
                                >
                                    <Form.Item
                                        name="resultLogoUrl"
                                        noStyle
                                    >
                                        <Input
                                            type="hidden"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        shouldUpdate
                                        noStyle
                                    >
                                        {() => {
                                            const value =
                                                form.getFieldValue(
                                                    'resultLogoUrl',
                                                )

                                            return (
                                                <Space
                                                    direction="vertical"
                                                    size={12}
                                                >
                                                    {value && (
                                                        <Image
                                                            src={
                                                                getImageUrl(
                                                                    value,
                                                                )
                                                            }
                                                            width={180}
                                                            style={{
                                                                maxHeight:
                                                                    100,
                                                                objectFit:
                                                                    'contain',
                                                            }}
                                                        />
                                                    )}

                                                    <Space wrap>
                                                        <Upload
                                                            accept="image/*"
                                                            showUploadList={
                                                                false
                                                            }
                                                            beforeUpload={
                                                                handleResultLogoUpload
                                                            }
                                                        >
                                                            <Button
                                                                icon={
                                                                    <UploadOutlined />
                                                                }
                                                                loading={
                                                                    uploadingResultLogo
                                                                }
                                                            >
                                                                {value
                                                                    ? 'Trocar logo'
                                                                    : 'Enviar logo'}
                                                            </Button>
                                                        </Upload>

                                                        {value && (
                                                            <Button
                                                                danger
                                                                onClick={() =>
                                                                    form.setFieldValue(
                                                                        'resultLogoUrl',
                                                                        '',
                                                                    )
                                                                }
                                                            >
                                                                Remover
                                                            </Button>
                                                        )}
                                                    </Space>
                                                </Space>
                                            )
                                        }}
                                    </Form.Item>
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={8}
                                lg={4}
                            >
                                <Form.Item
                                    label="Mostrar logo"
                                    name="resultShowLogo"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={8}
                                lg={5}
                            >
                                <Form.Item
                                    label="Largura (px)"
                                    name="resultLogoWidth"
                                >
                                    <InputNumber
                                        min={40}
                                        max={800}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={8}
                                lg={5}
                            >
                                <Form.Item
                                    label="Altura máxima"
                                    name="resultLogoHeight"
                                >
                                    <InputNumber
                                        min={40}
                                        max={500}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider />

                        {/* ================================================ */}
                        {/* TEXTOS */}
                        {/* ================================================ */}

                        <Title level={5}>
                            Textos
                        </Title>

                        <Row gutter={[20, 8]}>
                            <Col
                                xs={24}
                                lg={8}
                            >
                                <Form.Item
                                    label="Título ao concluir"
                                    name="resultCompletedTitle"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                lg={8}
                            >
                                <Form.Item
                                    label="Título se acabar o tempo"
                                    name="resultTimeoutTitle"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                lg={8}
                            >
                                <Form.Item
                                    label="Subtítulo"
                                    name="resultSubtitle"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[20, 8]}>
                            <Col
                                xs={24}
                                lg={6}
                            >
                                <Form.Item
                                    label="Tamanho do título"
                                    name="resultTitleFontSize"
                                >
                                    <InputNumber
                                        min={16}
                                        max={180}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider />

                        {/* ================================================ */}
                        {/* CARD DO RESULTADO */}
                        {/* ================================================ */}

                        <Title level={5}>
                            Card do resultado
                        </Title>

                        <Row gutter={[20, 8]}>
                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Fundo do card"
                                    name="resultCardBackground"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Cor dos textos"
                                    name="resultCardTextColor"
                                >
                                    <Input
                                        type="color"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Arredondamento"
                                    name="resultCardBorderRadius"
                                >
                                    <InputNumber
                                        min={0}
                                        max={100}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Sombra"
                                    name="resultCardShadow"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[20, 8]}>
                            <Col xs={12} md={6}>
                                <Form.Item
                                    label="Mostrar pontuação"
                                    name="resultShowScore"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>

                            <Col xs={12} md={6}>
                                <Form.Item
                                    label="Mostrar tempo"
                                    name="resultShowTime"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>

                            <Col xs={12} md={6}>
                                <Form.Item
                                    label="Mostrar tentativas"
                                    name="resultShowAttempts"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>

                            <Col xs={12} md={6}>
                                <Form.Item
                                    label="Mostrar erros"
                                    name="resultShowErrors"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider />

                        {/* ================================================ */}
                        {/* RANKING */}
                        {/* ================================================ */}

                        <Title level={5}>
                            Ranking
                        </Title>

                        <Row gutter={[20, 8]}>
                            <Col
                                xs={24}
                                lg={8}
                            >
                                <Form.Item
                                    label="Mostrar ranking"
                                    name="resultShowRanking"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                lg={8}
                            >
                                <Form.Item
                                    label="Título do ranking"
                                    name="resultRankingTitle"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                lg={8}
                            >
                                <Form.Item
                                    label="Quantidade de posições"
                                    name="resultRankingLimit"
                                >
                                    <InputNumber
                                        min={1}
                                        max={20}
                                        style={{
                                            width: '100%',
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider />

                        {/* ================================================ */}
                        {/* BOTÃO */}
                        {/* ================================================ */}

                        <Title level={5}>
                            Próximo participante
                        </Title>

                        <Row gutter={[20, 8]}>
                            <Col
                                xs={24}
                                lg={8}
                            >
                                <Form.Item
                                    label="Texto do botão"
                                    name="resultButtonText"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                lg={8}
                            >
                                <Form.Item
                                    label="Cor do botão"
                                    name="resultButtonColor"
                                >
                                    <Input
                                        type="color"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                lg={8}
                            >
                                <Form.Item
                                    label="Cor do texto"
                                    name="resultButtonTextColor"
                                >
                                    <Input
                                        type="color"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Divider />

                        <Title level={5}>
                            Redirecionamento externo
                        </Title>

                        <Row
                            gutter={[20, 8]}
                        >
                            <Col
                                xs={24}
                                lg={6}
                            >
                                <Form.Item
                                    label="Ativar redirecionamento externo"
                                    name="resultExternalRedirectEnabled"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                lg={10}
                            >
                                <Form.Item
                                    label="URL de destino"
                                    name="resultExternalRedirectUrl"
                                >
                                    <Input
                                        placeholder="https://www.exemplo.com.br"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                lg={8}
                            >
                                <Form.Item
                                    label="Texto do botão externo"
                                    name="resultExternalButtonText"
                                >
                                    <Input
                                        placeholder="ACESSAR"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row
                            gutter={[20, 8]}
                        >
                            <Col
                                xs={24}
                                lg={6}
                            >
                                <Form.Item
                                    label="Abrir em nova aba"
                                    name="resultExternalOpenNewTab"
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider />

                        <Button
                            type="primary"
                            size="large"
                            icon={
                                <SaveOutlined />
                            }
                            loading={
                                savingConfig
                            }
                            onClick={
                                handleSaveConfig
                            }
                        >
                            Salvar Resultado
                        </Button>

                    </Form>
                </TabPane >

                {/* ================================================== */}
                {/* TELA DE ESPERA */}
                {/* ================================================== */}

                <TabPane
                    tab="Tela de Espera"
                    key="waiting"
                >
                    <Card title="Personalização da tela de espera">
                        <Text type="secondary">
                            Aqui configuraremos a tela exibida entre participantes.
                        </Text>
                    </Card>
                </TabPane>

                {/* ================================================== */}
                {/* CARTAS */}
                {/* ================================================== */}

                <TabPane
                    tab={`Cartas (${cards.length})`}
                    key="cards"
                >
                    <CardsTab
                        selectedGameId={
                            selectedGameId
                        }
                        config={
                            config
                        }
                        cards={
                            cards
                        }
                        axiosConfig={
                            axiosConfig
                        }
                        reload={() =>
                            loadConfig(
                                selectedGameId,
                            )
                        }
                        updateConfigLocal={(
                            newConfig,
                        ) => {
                            setConfig(
                                newConfig,
                            )

                            if (
                                Array.isArray(
                                    newConfig?.cards,
                                )
                            ) {
                                setCards(
                                    newConfig.cards,
                                )
                            }
                        }}
                    />
                </TabPane>

                {/* ================================================== */}
                {/* ÁUDIO */}
                {/* ================================================== */}

                <TabPane
                    tab="Áudio"
                    key="audio"
                >
                    <Card
                        title="Áudio do jogo"
                        loading={
                            loadingConfig
                        }
                    >
                        <Form
                            form={
                                form
                            }
                            layout="vertical"
                        >
                            {/* ====================================== */}
                            {/* CONFIGURAÇÕES GERAIS */}
                            {/* ====================================== */}

                            <Row
                                gutter={[
                                    16,
                                    8,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Ativar efeitos sonoros"
                                        name="soundEnabled"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Volume dos efeitos"
                                        name="soundVolume"
                                    >
                                        <InputNumber
                                            min={0}
                                            max={1}
                                            step={0.1}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Ativar música"
                                        name="musicEnabled"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={6}
                                >
                                    <Form.Item
                                        label="Volume da música"
                                        name="musicVolume"
                                    >
                                        <InputNumber
                                            min={0}
                                            max={1}
                                            step={0.1}
                                            style={{
                                                width:
                                                    '100%',
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            {/* ====================================== */}
                            {/* EFEITOS */}
                            {/* ====================================== */}

                            <Title
                                level={5}
                                style={{
                                    marginBottom:
                                        16,
                                }}
                            >
                                Efeitos sonoros
                            </Title>

                            <Row
                                gutter={[
                                    16,
                                    16,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    md={12}
                                >
                                    <AudioField
                                        form={
                                            form
                                        }
                                        label="Som de virar carta"
                                        fieldName="flipSoundUrl"
                                        uploadingAudio={
                                            uploadingAudio
                                        }
                                        handleAudioUpload={
                                            handleAudioUpload
                                        }
                                        previewAudio={
                                            previewAudio
                                        }
                                    />
                                </Col>

                                <Col
                                    xs={24}
                                    md={12}
                                >
                                    <AudioField
                                        form={
                                            form
                                        }
                                        label="Som de acerto"
                                        fieldName="matchSoundUrl"
                                        uploadingAudio={
                                            uploadingAudio
                                        }
                                        handleAudioUpload={
                                            handleAudioUpload
                                        }
                                        previewAudio={
                                            previewAudio
                                        }
                                    />
                                </Col>

                                <Col
                                    xs={24}
                                    md={12}
                                >
                                    <AudioField
                                        form={
                                            form
                                        }
                                        label="Som de erro"
                                        fieldName="errorSoundUrl"
                                        uploadingAudio={
                                            uploadingAudio
                                        }
                                        handleAudioUpload={
                                            handleAudioUpload
                                        }
                                        previewAudio={
                                            previewAudio
                                        }
                                    />
                                </Col>

                                <Col
                                    xs={24}
                                    md={12}
                                >
                                    <AudioField
                                        form={
                                            form
                                        }
                                        label="Som da contagem regressiva"
                                        fieldName="countdownSoundUrl"
                                        uploadingAudio={
                                            uploadingAudio
                                        }
                                        handleAudioUpload={
                                            handleAudioUpload
                                        }
                                        previewAudio={
                                            previewAudio
                                        }
                                    />
                                </Col>

                                <Col
                                    xs={24}
                                    md={12}
                                >
                                    <AudioField
                                        form={
                                            form
                                        }
                                        label="Som do Valendo"
                                        fieldName="goSoundUrl"
                                        uploadingAudio={
                                            uploadingAudio
                                        }
                                        handleAudioUpload={
                                            handleAudioUpload
                                        }
                                        previewAudio={
                                            previewAudio
                                        }
                                    />
                                </Col>

                                <Col
                                    xs={24}
                                    md={12}
                                >
                                    <AudioField
                                        form={
                                            form
                                        }
                                        label="Som de conclusão"
                                        fieldName="completeSoundUrl"
                                        uploadingAudio={
                                            uploadingAudio
                                        }
                                        handleAudioUpload={
                                            handleAudioUpload
                                        }
                                        previewAudio={
                                            previewAudio
                                        }
                                    />
                                </Col>
                            </Row>

                            <Divider />

                            {/* ====================================== */}
                            {/* MÚSICA */}
                            {/* ====================================== */}

                            <Title
                                level={5}
                                style={{
                                    marginBottom:
                                        16,
                                }}
                            >
                                Música de fundo
                            </Title>

                            <Row>
                                <Col
                                    xs={24}
                                >
                                    <AudioField
                                        form={
                                            form
                                        }
                                        label="Arquivo da música"
                                        fieldName="musicUrl"
                                        uploadingAudio={
                                            uploadingAudio
                                        }
                                        handleAudioUpload={
                                            handleAudioUpload
                                        }
                                        previewAudio={
                                            previewAudio
                                        }
                                    />
                                </Col>
                            </Row>

                            <Button
                                type="primary"
                                icon={
                                    <SaveOutlined />
                                }
                                loading={
                                    savingConfig
                                }
                                onClick={
                                    handleSaveConfig
                                }
                            >
                                Salvar áudio
                            </Button>
                        </Form>
                    </Card>
                </TabPane>

                {/* ================================================== */}
                {/* HISTÓRICO */}
                {/* ================================================== */}

                <TabPane
                    tab="Histórico"
                    key="history"
                >
                    <Card
                        title="Histórico de partidas"
                        loading={
                            loadingMatches
                        }
                        extra={
                            <Space>
                                <Select
                                    value={
                                        matchesLimit
                                    }
                                    style={{
                                        width:
                                            140,
                                    }}
                                    onChange={(
                                        value,
                                    ) =>
                                        setMatchesLimit(
                                            value,
                                        )
                                    }
                                    options={[
                                        {
                                            value:
                                                50,
                                            label:
                                                'Últimas 50',
                                        },
                                        {
                                            value:
                                                100,
                                            label:
                                                'Últimas 100',
                                        },
                                        {
                                            value:
                                                250,
                                            label:
                                                'Últimas 250',
                                        },
                                        {
                                            value:
                                                500,
                                            label:
                                                'Últimas 500',
                                        },
                                    ]}
                                />

                                <Button
                                    icon={
                                        <ReloadOutlined />
                                    }
                                    loading={
                                        loadingMatches
                                    }
                                    disabled={
                                        !selectedGameId
                                    }
                                    onClick={() =>
                                        loadMatches(
                                            selectedGameId,
                                            matchesLimit,
                                        )
                                    }
                                >
                                    Atualizar
                                </Button>
                            </Space>
                        }
                    >
                        <Row
                            gutter={[
                                16,
                                16,
                            ]}
                            style={{
                                marginBottom:
                                    20,
                            }}
                        >
                            {/* ========================================== */}
                            {/* BUSCA */}
                            {/* ========================================== */}

                            <Col
                                xs={24}
                                md={10}
                            >
                                <Text strong>
                                    Participante
                                </Text>

                                <Input
                                    allowClear
                                    value={
                                        historySearch
                                    }
                                    placeholder="Nome, e-mail, telefone ou CPF"
                                    style={{
                                        marginTop:
                                            8,
                                    }}
                                    onChange={(
                                        event,
                                    ) =>
                                        setHistorySearch(
                                            event.target.value,
                                        )
                                    }
                                />
                            </Col>

                            {/* ========================================== */}
                            {/* STATUS */}
                            {/* ========================================== */}

                            <Col
                                xs={24}
                                md={7}
                            >
                                <Text strong>
                                    Status
                                </Text>

                                <Select
                                    value={
                                        historyStatus
                                    }
                                    style={{
                                        width:
                                            '100%',

                                        marginTop:
                                            8,
                                    }}
                                    onChange={
                                        setHistoryStatus
                                    }
                                    options={[
                                        {
                                            value:
                                                'ALL',
                                            label:
                                                'Todas as partidas',
                                        },
                                        {
                                            value:
                                                'COMPLETED',
                                            label:
                                                'Concluídas',
                                        },
                                        {
                                            value:
                                                'NOT_COMPLETED',
                                            label:
                                                'Não concluídas',
                                        },
                                    ]}
                                />
                            </Col>

                            {/* ========================================== */}
                            {/* DATA */}
                            {/* ========================================== */}

                            <Col
                                xs={24}
                                md={7}
                            >
                                <Text strong>
                                    Data
                                </Text>

                                <DatePicker
                                    value={
                                        historyDate
                                    }
                                    format="DD/MM/YYYY"
                                    placeholder="Todas as datas"
                                    allowClear
                                    style={{
                                        width:
                                            '100%',

                                        marginTop:
                                            8,
                                    }}
                                    onChange={
                                        setHistoryDate
                                    }
                                />
                            </Col>
                        </Row>


                        {/* ================================================== */}
                        {/* QUANTIDADE DE RESULTADOS DO HISTÓRICO */}
                        {/* ================================================== */}

                        {selectedGameId && (
                            <div
                                style={{
                                    marginBottom:
                                        14,
                                }}
                            >
                                <Text type="secondary">
                                    {
                                        filteredMatches.length
                                    }{' '}
                                    partida(s) encontrada(s)
                                </Text>
                            </div>
                        )}

                        {!selectedGameId ? (
                            <Text type="secondary">
                                Selecione um jogo para visualizar o histórico.
                            </Text>
                        ) : (
                            <Table
                                rowKey="id"
                                columns={
                                    matchesColumns
                                }
                                dataSource={
                                    filteredMatches
                                }
                                pagination={{
                                    pageSize:
                                        25,

                                    showSizeChanger:
                                        true,

                                    pageSizeOptions: [
                                        '25',
                                        '50',
                                        '100',
                                    ],
                                }}
                                scroll={{
                                    x:
                                        950,
                                }}
                                locale={{
                                    emptyText:
                                        'Nenhuma partida registrada.',
                                }}
                            />
                        )}
                    </Card>
                </TabPane>

                {/* ================================================== */}
                {/* RANKING */}
                {/* ================================================== */}

                <TabPane
                    tab="Ranking"
                    key="ranking"
                >
                    <Card
                        title="Ranking"
                        loading={
                            loadingRanking
                        }
                        extra={
                            <Space>
                                <Select
                                    value={
                                        rankingLimit
                                    }
                                    style={{
                                        width:
                                            130,
                                    }}
                                    onChange={(
                                        value,
                                    ) =>
                                        setRankingLimit(
                                            value,
                                        )
                                    }
                                    options={[
                                        {
                                            value:
                                                10,
                                            label:
                                                'Top 10',
                                        },
                                        {
                                            value:
                                                20,
                                            label:
                                                'Top 20',
                                        },
                                        {
                                            value:
                                                50,
                                            label:
                                                'Top 50',
                                        },
                                        {
                                            value:
                                                100,
                                            label:
                                                'Top 100',
                                        },
                                    ]}
                                />

                                <Button
                                    icon={
                                        <ReloadOutlined />
                                    }
                                    loading={
                                        loadingRanking
                                    }
                                    disabled={
                                        !selectedGameId
                                    }
                                    onClick={() =>
                                        loadRanking(
                                            selectedGameId,
                                            rankingLimit,
                                        )
                                    }
                                >
                                    Atualizar
                                </Button>
                                <Popconfirm
                                    title="Zerar ranking?"
                                    description={
                                        ranking.length > 0
                                            ? 'Todas as partidas deste jogo serão excluídas. Participantes e leads serão mantidos.'
                                            : 'O ranking deste jogo será limpo.'
                                    }
                                    okText="Sim, zerar"
                                    cancelText="Cancelar"
                                    okButtonProps={{
                                        danger:
                                            true,
                                    }}
                                    onConfirm={
                                        handleClearRanking
                                    }
                                >
                                    <Button
                                        danger
                                        icon={
                                            <ClearOutlined />
                                        }
                                        loading={
                                            clearingRanking
                                        }
                                        disabled={
                                            !selectedGameId ||
                                            clearingRanking
                                        }
                                    >
                                        Zerar ranking
                                    </Button>
                                </Popconfirm>
                            </Space>
                        }
                    >
                        {!selectedGameId ? (
                            <Text type="secondary">
                                Selecione um jogo para visualizar o ranking.
                            </Text>
                        ) : (
                            <Table
                                rowKey="id"
                                columns={
                                    rankingColumns
                                }
                                dataSource={
                                    ranking
                                }
                                loading={
                                    loadingRanking
                                }
                                pagination={
                                    false
                                }
                                scroll={{
                                    x:
                                        850,
                                }}
                                locale={{
                                    emptyText:
                                        'Nenhuma partida concluída no ranking.',
                                }}
                            />
                        )}
                    </Card>
                </TabPane>
            </Tabs>

            {/* ====================================================== */}
            {/* MODAL - REGRA DE PREMIAÇÃO */}
            {/* ====================================================== */}

            <Modal
                title={
                    editingPrizeRule
                        ? 'Editar regra de premiação'
                        : 'Nova regra de premiação'
                }
                open={
                    prizeRuleModalOpen
                }
                onCancel={() => {
                    setPrizeRuleModalOpen(
                        false,
                    )

                    setEditingPrizeRule(
                        null,
                    )

                    prizeRuleForm.resetFields()
                }}
                onOk={
                    handleSavePrizeRule
                }
                confirmLoading={
                    savingPrizeRule
                }
                okText={
                    editingPrizeRule
                        ? 'Salvar alterações'
                        : 'Criar regra'
                }
                cancelText="Cancelar"
                width={
                    720
                }
                destroyOnClose
            >
                <Form
                    form={
                        prizeRuleForm
                    }
                    layout="vertical"
                >

                    {/* ====================================================== */}
                    {/* ORIGEM DOS BRINDES */}
                    {/* ====================================================== */}

                    <Form.Item
                        label="Origem dos brindes"
                    >
                        <Select
                            value={
                                prizeSourceType
                            }
                            options={[
                                {
                                    value:
                                        'MEMORY',
                                    label:
                                        'Brindes deste Jogo da Memória',
                                },
                                {
                                    value:
                                        'ROULETTE',
                                    label:
                                        'Brindes de uma Roleta',
                                },
                            ]}
                            onChange={async (
                                value,
                            ) => {
                                setPrizeSourceType(
                                    value,
                                )

                                prizeRuleForm.setFieldsValue({
                                    prizeId:
                                        undefined,
                                })

                                setAvailablePrizes(
                                    [],
                                )

                                if (
                                    value ===
                                    'MEMORY'
                                ) {
                                    setPrizeSourceGameId(
                                        selectedGameId,
                                    )

                                    await loadAvailablePrizes(
                                        selectedGameId,
                                    )

                                    return
                                }

                                // Selecionou Roleta.
                                // Esperamos a escolha da roleta
                                // antes de carregar os brindes.
                                setPrizeSourceGameId(
                                    null,
                                )

                                await loadRouletteGames()
                            }}
                        />
                    </Form.Item>

                    {/* ====================================================== */}
                    {/* SELEÇÃO DA ROLETA */}
                    {/* ====================================================== */}

                    {prizeSourceType ===
                        'ROULETTE' && (
                            <Form.Item
                                label="Roleta"
                                required
                            >
                                <Select
                                    value={
                                        prizeSourceGameId ||
                                        undefined
                                    }
                                    loading={
                                        loadingRouletteGames
                                    }
                                    placeholder="Selecione a roleta"
                                    showSearch
                                    optionFilterProp="label"
                                    options={
                                        rouletteGames.map(
                                            (
                                                roulette,
                                            ) => ({
                                                value:
                                                    roulette.id,

                                                label:
                                                    roulette.name ||
                                                    roulette.title ||
                                                    'Roleta',
                                            }),
                                        )
                                    }
                                    onChange={async (
                                        rouletteId,
                                    ) => {
                                        setPrizeSourceGameId(
                                            rouletteId,
                                        )

                                        prizeRuleForm.setFieldsValue({
                                            prizeId:
                                                undefined,
                                        })

                                        setAvailablePrizes(
                                            [],
                                        )

                                        await loadAvailablePrizes(
                                            rouletteId,
                                        )
                                    }}
                                />
                            </Form.Item>
                        )}

                    {/* ====================================================== */}
                    {/* BRINDE */}
                    {/* ====================================================== */}

                    <Form.Item
                        label="Brinde"
                        name="prizeId"
                        rules={[
                            {
                                required:
                                    true,
                                message:
                                    'Selecione o brinde.',
                            },
                        ]}
                    >
                        <Select
                            showSearch
                            allowClear
                            loading={
                                loadingPrizes
                            }
                            disabled={
                                prizeSourceType ===
                                'ROULETTE' &&
                                !prizeSourceGameId
                            }
                            placeholder={
                                prizeSourceType ===
                                    'ROULETTE' &&
                                    !prizeSourceGameId
                                    ? 'Primeiro selecione a roleta'
                                    : 'Selecione o brinde'
                            }
                            optionFilterProp="label"
                            notFoundContent={
                                loadingPrizes
                                    ? 'Carregando brindes...'
                                    : 'Nenhum brinde encontrado'
                            }
                            options={
                                availablePrizes.map(
                                    (
                                        prize,
                                    ) => ({
                                        value:
                                            prize.id,

                                        label:
                                            `${prize.name || 'Brinde'} — Estoque: ${prize.stockAvailable ?? 0}`,
                                    }),
                                )
                            }
                        />
                    </Form.Item>

                    <Row
                        gutter={[
                            16,
                            8,
                        ]}
                    >
                        {/* ========================================== */}
                        {/* PRIORIDADE */}
                        {/* ========================================== */}

                        <Col
                            xs={24}
                            md={12}
                        >
                            <Form.Item
                                label="Prioridade"
                                name="priority"
                                tooltip="Regras com maior prioridade são verificadas primeiro."
                                rules={[
                                    {
                                        required:
                                            true,
                                        message:
                                            'Informe a prioridade.',
                                    },
                                ]}
                            >
                                <InputNumber
                                    min={
                                        1
                                    }
                                    max={
                                        999
                                    }
                                    style={{
                                        width:
                                            '100%',
                                    }}
                                />
                            </Form.Item>
                        </Col>

                        {/* ========================================== */}
                        {/* STATUS */}
                        {/* ========================================== */}

                        <Col
                            xs={24}
                            md={12}
                        >
                            <Form.Item
                                label="Regra ativa"
                                name="isActive"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider />

                    <Text
                        strong
                    >
                        Condições para ganhar este brinde
                    </Text>

                    <div
                        style={{
                            marginTop:
                                16,
                        }}
                    >
                        <Row
                            gutter={[
                                16,
                                8,
                            ]}
                        >
                            {/* ====================================== */}
                            {/* PONTUAÇÃO */}
                            {/* ====================================== */}

                            <Col
                                xs={24}
                                md={8}
                            >
                                <Form.Item
                                    label="Pontuação mínima"
                                    name="minScore"
                                    tooltip="Deixe vazio se a pontuação não fizer parte desta regra."
                                >
                                    <InputNumber
                                        min={
                                            0
                                        }
                                        style={{
                                            width:
                                                '100%',
                                        }}
                                        placeholder="Ex.: 1000"
                                    />
                                </Form.Item>
                            </Col>

                            {/* ====================================== */}
                            {/* TENTATIVAS */}
                            {/* ====================================== */}

                            <Col
                                xs={24}
                                md={8}
                            >
                                <Form.Item
                                    label="Máximo de tentativas"
                                    name="maxAttempts"
                                    tooltip="O participante precisa terminar usando no máximo esta quantidade de tentativas."
                                >
                                    <InputNumber
                                        min={
                                            1
                                        }
                                        style={{
                                            width:
                                                '100%',
                                        }}
                                        placeholder="Ex.: 10"
                                    />
                                </Form.Item>
                            </Col>

                            {/* ====================================== */}
                            {/* TEMPO */}
                            {/* ====================================== */}

                            <Col
                                xs={24}
                                md={8}
                            >
                                <Form.Item
                                    label="Tempo máximo (ms)"
                                    name="maxTimeMs"
                                    tooltip="Exemplo: 30000 = 30 segundos."
                                >
                                    <InputNumber
                                        min={
                                            1000
                                        }
                                        step={
                                            1000
                                        }
                                        style={{
                                            width:
                                                '100%',
                                        }}
                                        placeholder="Ex.: 30000"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <Divider />

                    {/* ============================================== */}
                    {/* PESO */}
                    {/* ============================================== */}

                    <Form.Item
                        label="Peso de probabilidade"
                        name="probabilityWeight"
                        tooltip="Usado quando houver mais de um brinde elegível para o mesmo resultado."
                    >
                        <InputNumber
                            min={
                                0
                            }
                            max={
                                100000
                            }
                            style={{
                                width:
                                    '100%',
                            }}
                        />
                    </Form.Item>

                    <Alert
                        type="info"
                        showIcon
                        message="Exemplo"
                        description="Para entregar este brinde a quem fizer pelo menos 1.000 pontos em até 10 tentativas e 30 segundos, informe: Pontuação mínima = 1000, Tentativas = 10 e Tempo máximo = 30000."
                    />
                </Form>
            </Modal>

        </>
    )
}

export default MemorySettings